import React, { useEffect, useState, Children, Fragment, Component } from 'react';
import { ArrowLeft, Clock, FileText, FileCheck, User, Users, Heart, Gift, Shield, CheckCircle, Eye, Download, Plus, Trash2, HelpCircle, Briefcase, Star, DollarSign, Home, XCircle, ChevronRight, ChevronDown, Crown, Lock, Save, Pencil, Share2, PenLine, Check } from 'lucide-react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
// Components
import { AutosaveIndicator } from '../components/wills/AutosaveIndicator';
import { HelperBlock } from '../components/wills/HelperBlock';
import { QuestionCard } from '../components/wills/QuestionCard';
import { PhaseStrip } from '../components/wills/PhaseStrip';
import { PersonSelector } from '../components/wills/PersonSelector';
import { SummaryCard } from '../components/wills/SummaryCard';
import { LegalPreview } from '../components/wills/LegalPreview';
import { ReadinessWidget } from '../components/wills/ReadinessWidget';
import { StickyNavFooter } from '../components/wills/StickyNavFooter';
import { ResumeDraftCard } from '../components/wills/ResumeDraftCard';
import { ReviewSection } from '../components/wills/ReviewSection';
import { MobilePreviewSheet } from '../components/wills/MobilePreviewSheet';
interface DashboardContext {
  toggleMobileSidebar: () => void;
}
interface WillsProps {
  onMenuClick?: () => void;
  onNavigateToAICopilot?: () => void;
  onNavigate?: (path: string) => void;
}
type ViewState = 'landing' | 'setup' | 'resume' | 'builder' | 'review';
type PreviewMode = 'summary' | 'legal';
type DraftStatus = 'not_started' | 'in_progress' | 'needs_review' | 'completed' | 'submitted_for_review' | 'locked';
type SaveStatus = 'saving' | 'saved' | 'saved_ago' | 'offline' | 'error';
type MilestoneStatus = 'locked' | 'available' | 'in_progress' | 'completed' | 'skipped' | 'needs_attention' | 'flagged';
interface Child {
  id: string;
  name: string;
  age: string;
}
interface Beneficiary {
  id: string;
  name: string;
  percentage: number;
}
interface AlternateExecutor {
  id: string;
  relationship: string;
  legalName: string;
}
interface PersonalPropertyGift {
  id: string;
  category: 'jewellery' | 'art' | 'collectibles' | 'other' | '';
  description: string;
  quantity: string;
  recipientRelationship: string;
  recipientLegalName: string;
}
interface ArtCollectible {
  id: string;
  description: string;
  estimatedValue: string;
  recipientRelationship: string;
  recipientLegalName: string;
}
interface FinancialGift {
  id: string;
  type: 'lump_sum' | 'percentage' | '';
  amount: string;
  recipientRelationship: string;
  recipientLegalName: string;
}
interface CharityGift {
  id: string;
  charityName: string;
  amount: string;
  purpose: string;
}
interface RealEstateGift {
  id: string;
  propertyAddress: string;
  propertyType: string;
  recipientRelationship: string;
  recipientLegalName: string;
}
interface WillEntry {
  id: string;
  title: string;
  type: 'personal' | 'business';
  status: DraftStatus;
  progress: number;
  lastEdited: string;
}
interface FormData {
  // About You
  fullName: string;
  dob: string;
  province: string;
  address: string;
  // Personal Background
  citizenship: string;
  immigrationStatus: 'citizen' | 'permanent_resident' | 'temporary_resident' | 'other' | '';
  cityOfBirth: string;
  countryOfBirth: string;
  occupation: string;
  employer: string;
  businessAddress: string;
  businessPhone: string;
  // Family & Marital Context
  maritalStatus: 'single' | 'married' | 'common-law' | 'divorced' | 'widowed' | '';
  spouseLegalName: string;
  hasChildren: boolean | null;
  children: Child[];
  // Executors
  executor: string;
  alternateExecutors: AlternateExecutor[];
  executorNotSure: boolean;
  // Funeral Wishes
  funeralArrangement: 'burial' | 'cremation' | 'leave_to_executor' | '';
  ashesInstructions: string;
  specialFuneralInstructions: string;
  // Personal Gifts
  hasPersonalProperty: boolean | null;
  personalPropertyGifts: PersonalPropertyGift[];
  // Special Assets
  hasArtCollectibles: boolean | null;
  artCollectibles: ArtCollectible[];
  // Financial & Charity Gifts
  hasFinancialGifts: boolean | null;
  financialGifts: FinancialGift[];
  hasCharityGifts: boolean | null;
  charityGifts: CharityGift[];
  distributionType: 'spouse_first' | 'split' | 'custom' | '';
  beneficiaries: Beneficiary[];
  // Real Estate
  hasRealEstateGifts: boolean | null;
  realEstateGifts: RealEstateGift[];
}
const MILESTONES = [{
  id: 'about',
  label: 'About You',
  icon: User,
  questions: 3
}, {
  id: 'background',
  label: 'Personal Background',
  icon: Briefcase,
  questions: 4
}, {
  id: 'family',
  label: 'Family & Marital',
  icon: Users,
  questions: 3
}, {
  id: 'executor',
  label: 'Executors',
  icon: Shield,
  questions: 3
}, {
  id: 'funeral',
  label: 'Funeral Wishes',
  icon: Heart,
  questions: 3
}, {
  id: 'personalGifts',
  label: 'Personal Gifts',
  icon: Gift,
  questions: 2
}, {
  id: 'specialAssets',
  label: 'Special Assets',
  icon: Star,
  questions: 2
}, {
  id: 'financialGifts',
  label: 'Financial & Charity',
  icon: DollarSign,
  questions: 5
}, {
  id: 'realEstate',
  label: 'Real Estate',
  icon: Home,
  questions: 2
}, {
  id: 'review',
  label: 'Review & Finalize',
  icon: CheckCircle,
  questions: 0
}];
export function Wills({
  onMenuClick,
  onNavigateToAICopilot,
  onNavigate
}: WillsProps) {
  const navigate = useNavigate();
  const {
    toggleMobileSidebar
  } = useOutletContext<DashboardContext>() || {
    toggleMobileSidebar: () => {}
  };
  const [viewState, setViewState] = useState<ViewState>('landing');
  const [currentPhaseId, setCurrentPhaseId] = useState<string>('basics');
  const [currentMilestoneIndex, setCurrentMilestoneIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('summary');
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  const [recentlyUpdatedSection, setRecentlyUpdatedSection] = useState<string | null>(null);
  const [showDraftUpdatedNotification, setShowDraftUpdatedNotification] = useState(false);
  // Removed reviewActiveTab state since tabs are removed
  const [draftStatus, setDraftStatus] = useState<DraftStatus>('in_progress');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [lastSaved, setLastSaved] = useState<Date>(new Date(Date.now() - 1000 * 60 * 60 * 24 * 2));
  const [lastSavedMinutesAgo, setLastSavedMinutesAgo] = useState(0);
  const [showCompletionFeedback, setShowCompletionFeedback] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [expandedReviewSections, setExpandedReviewSections] = useState<Record<string, boolean>>({});
  const [showCustomExecutorInput, setShowCustomExecutorInput] = useState(false);
  const [showExecutorWarning, setShowExecutorWarning] = useState(false);
  const [customExecutorName, setCustomExecutorName] = useState('');
  const [documentTitle, setDocumentTitle] = useState('Last Will and Testament');
  const [isRenamingDoc, setIsRenamingDoc] = useState(false);
  const [draftTitle, setDraftTitle] = useState(documentTitle);
  const [formData, setFormData] = useState<FormData>({
    fullName: 'John Michael Doe',
    dob: '1980-05-15',
    province: 'Ontario',
    address: '47 King Street West, Toronto, ON M5H 1J8.',
    citizenship: 'Canadian',
    immigrationStatus: 'citizen',
    cityOfBirth: 'Toronto',
    countryOfBirth: 'Canada',
    occupation: 'Software Engineer',
    employer: 'Maple Tech Solutions Inc.',
    businessAddress: '200 Bay Street, Suite 1500, Toronto, ON M5J 2J2.',
    businessPhone: '+1 (416) 555-0199',
    maritalStatus: 'married',
    spouseLegalName: 'Jessica Anne Doe',
    hasChildren: true,
    children: [{
      id: '1',
      name: 'Emma Doe',
      age: '12'
    }, {
      id: '2',
      name: 'Lucas Doe',
      age: '8'
    }],
    executor: '',
    alternateExecutors: [],
    executorNotSure: false,
    funeralArrangement: '',
    ashesInstructions: '',
    specialFuneralInstructions: '',
    hasPersonalProperty: null,
    personalPropertyGifts: [],
    hasArtCollectibles: null,
    artCollectibles: [],
    hasFinancialGifts: null,
    financialGifts: [],
    hasCharityGifts: null,
    charityGifts: [],
    distributionType: '',
    beneficiaries: [{
      id: '1',
      name: 'Jessica Doe',
      percentage: 60
    }, {
      id: '2',
      name: 'Emma Doe',
      percentage: 20
    }, {
      id: '3',
      name: 'Lucas Doe',
      percentage: 20
    }],
    hasRealEstateGifts: null,
    realEstateGifts: []
  });
  const [milestoneStatuses, setMilestoneStatuses] = useState<Record<string, MilestoneStatus>>({
    about: 'completed',
    background: 'completed',
    family: 'completed',
    executor: 'in_progress',
    funeral: 'available',
    personalGifts: 'locked',
    specialAssets: 'locked',
    financialGifts: 'locked',
    realEstate: 'locked',
    review: 'locked'
  });
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showWillPreview, setShowWillPreview] = useState(false);
  const [activeWillId, setActiveWillId] = useState<string | null>('will-1');
  const [myWills, setMyWills] = useState<WillEntry[]>([{
    id: 'will-1',
    title: 'Last Will and Testament',
    type: 'personal',
    status: 'in_progress',
    progress: 33,
    lastEdited: 'Last edited 2 days ago'
  }, {
    id: 'will-2',
    title: 'Secondary Will — Investment Portfolio',
    type: 'personal',
    status: 'completed',
    progress: 100,
    lastEdited: 'Last edited 1 week ago'
  }]);
  const handleDeleteWill = (willId: string) => {
    setMyWills(prev => prev.filter(w => w.id !== willId));
    setConfirmDeleteId(null);
    if (activeWillId === willId) {
      setActiveWillId(null);
    }
  };
  const toggleFaq = (id: string) => {
    if (expandedFaq === id) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(id);
    }
  };
  const renderLandingState = () => {
    const renderStatusBadge = (status: DraftStatus) => {
      switch (status) {
        case 'not_started':
          return <span className="px-3 py-1 bg-disabled/20 text-secondary rounded-full text-xs font-semibold tracking-wide uppercase" data-id="element-4136">
              Not Started
            </span>;
        case 'in_progress':
          return <span className="px-3 py-1 bg-violetBlue/10 text-violetBlue rounded-full text-xs font-semibold tracking-wide uppercase" data-id="element-4137">
              In Progress
            </span>;
        case 'needs_review':
          return <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold tracking-wide uppercase" data-id="element-4138">
              Needs Review
            </span>;
        case 'completed':
          return <span className="px-3 py-1 bg-turquoise/10 text-turquoise rounded-full text-xs font-semibold tracking-wide uppercase" data-id="element-4139">
              Completed
            </span>;
        case 'submitted_for_review':
          return <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold tracking-wide uppercase" data-id="element-4140">
              Under Legal Review
            </span>;
        default:
          return null;
      }
    };
    // Mock subscription plan data
    const subscriptionPlan = {
      name: 'LawVo Pro',
      maxWills: 3
    };
    const faqs = [{
      id: 'faq1',
      question: 'Do I need a lawyer to create a Will?',
      answer: 'In Canada, you do not legally need a lawyer to write a Will. A Will is legally valid as long as it meets the requirements of your province (typically, it must be in writing, signed by you, and witnessed by two people who are not beneficiaries). However, for complex estates, legal advice is recommended.'
    }, {
      id: 'faq2',
      question: 'Is a digital Will legally valid in Canada?',
      answer: 'Currently, most Canadian provinces require a Will to be a physical, printed document with wet-ink signatures. British Columbia is the only province that fully accepts purely digital Wills. For other provinces, you must print the document generated by LawVo and sign it physically with witnesses.'
    }, {
      id: 'faq3',
      question: 'How often should I update my Will?',
      answer: 'You should review your Will every 3-5 years, or whenever you experience a major life event such as marriage, divorce, the birth of a child, a significant change in financial status, or the death of an executor or beneficiary.'
    }, {
      id: 'faq4',
      question: 'What happens if I die without a Will?',
      answer: 'Dying without a Will is called dying "intestate." If this happens, your province\'s laws will dictate how your assets are divided, which may not align with your wishes. It can also cause delays, extra costs, and stress for your family, and the court will appoint an executor and guardians for minor children.'
    }];
    return <div className="flex-1 w-full bg-white overflow-y-auto" data-id="element-4141">
        <div className="w-full px-4 sm:px-6 lg:pl-[100px] lg:pr-8 py-8 sm:py-12 max-w-[1400px]" data-id="element-4142">
          {/* Hero Section */}
          <div className="mb-10" data-id="element-4143">
            <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2 tracking-tight" data-id="element-4144">
              Wills & Estate Planning
            </h1>
            <p className="text-sm sm:text-base text-secondary leading-relaxed max-w-2xl" data-id="element-4145">
              Protect your family and ensure your wishes are legally binding in
              Canada. Create a comprehensive, province-specific Will in minutes
              without the high legal fees.
            </p>
          </div>

          {/* Start a New Will */}
          <div className="mb-12" data-id="element-4146">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4147">
              {/* Personal Will Card */}
              {(() => {
              const isLimitReached = myWills.length >= subscriptionPlan.maxWills;
              return <div className={`bg-white rounded-2xl border shadow-sm p-6 sm:p-8 flex flex-col h-full transition-shadow relative ${isLimitReached ? 'border-borderColor' : 'border-borderColor hover:shadow-md'}`} data-id="element-4148">
                    {isLimitReached && <div className="mb-4 sm:mb-0 sm:absolute sm:top-6 sm:right-6" data-id="element-4149">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold tracking-wide uppercase" data-id="element-4150">
                          <Crown className="w-3 h-3" data-id="element-4151" />
                          Limit Reached
                        </span>
                      </div>}
                    <div className="flex items-center gap-4 mb-4" data-id="element-4152">
                      <div className={`p-3 rounded-xl ${isLimitReached ? 'bg-violetBlue/5' : 'bg-violetBlue/10'}`} data-id="element-4153">
                        <User className={`w-6 h-6 ${isLimitReached ? 'text-violetBlue/50' : 'text-violetBlue'}`} data-id="element-4154" />
                      </div>
                      <h3 className="text-xl font-bold text-primary" data-id="element-4155">
                        Personal Will
                      </h3>
                    </div>
                    <p className="text-sm text-secondary mb-6" data-id="element-4156">
                      Create a legally valid Last Will and Testament for
                      yourself to protect your personal assets and family.
                    </p>
                    <ul className={`space-y-3 mb-8 flex-1 ${isLimitReached ? 'opacity-60' : ''}`} data-id="element-4157">
                      <li className="flex items-start gap-2.5 text-sm text-secondary" data-id="element-4158">
                        <CheckCircle className="w-4 h-4 text-turquoise flex-shrink-0 mt-0.5" data-id="element-4159" />
                        <span data-id="element-4160">Appoint executors and alternate executors</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-sm text-secondary" data-id="element-4161">
                        <CheckCircle className="w-4 h-4 text-turquoise flex-shrink-0 mt-0.5" data-id="element-4162" />
                        <span data-id="element-4163">Distribute assets and specific gifts</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-sm text-secondary" data-id="element-4164">
                        <CheckCircle className="w-4 h-4 text-turquoise flex-shrink-0 mt-0.5" data-id="element-4165" />
                        <span data-id="element-4166">Appoint guardians for minor children</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-sm text-secondary" data-id="element-4167">
                        <CheckCircle className="w-4 h-4 text-turquoise flex-shrink-0 mt-0.5" data-id="element-4168" />
                        <span data-id="element-4169">Outline funeral and burial wishes</span>
                      </li>
                    </ul>
                    {isLimitReached ? <div className="space-y-3" data-id="element-4170">
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-4171">
                          <p className="text-xs text-amber-800 leading-relaxed" data-id="element-4172">
                            You've used all{' '}
                            <span className="font-semibold" data-id="element-4173">
                              {subscriptionPlan.maxWills} wills
                            </span>{' '}
                            included in your {subscriptionPlan.name} plan.
                            Upgrade to create more.
                          </p>
                        </div>
                        <button onClick={() => setShowUpgradeModal(true)} className="w-full bg-violetBlue text-white py-3.5 rounded-xl font-semibold hover:bg-[#6844E8] transition-colors flex items-center justify-center gap-2" data-id="element-4174">
                          <Crown className="w-4 h-4" data-id="element-4175" />
                          Upgrade Plan
                        </button>
                      </div> : <button onClick={() => setViewState('setup')} className="w-full bg-violetBlue text-white py-3.5 rounded-xl font-semibold hover:bg-[#6844E8] transition-colors flex items-center justify-center gap-2" data-id="element-4176">
                        Start Personal Will
                        <ChevronRight className="w-4 h-4" data-id="element-4177" />
                      </button>}
                  </div>;
            })()}

              {/* Business Will Card */}
              <div className="bg-white rounded-2xl border border-borderColor shadow-sm p-6 sm:p-8 flex flex-col h-full relative overflow-hidden" data-id="element-4178">
                <div className="mb-4 sm:mb-0 sm:absolute sm:top-6 sm:right-6" data-id="element-4179">
                  <span className="inline-block px-3 py-1 bg-disabled/20 text-secondary rounded-full text-xs font-semibold tracking-wide uppercase" data-id="element-4180">
                    Coming Soon
                  </span>
                </div>
                <div className="flex items-center gap-4 mb-4 opacity-60" data-id="element-4181">
                  <div className="p-3 bg-secondary/10 rounded-xl" data-id="element-4182">
                    <Briefcase className="w-6 h-6 text-secondary" data-id="element-4183" />
                  </div>
                  <h3 className="text-xl font-bold text-primary" data-id="element-4184">
                    Business Will
                  </h3>
                </div>
                <p className="text-sm text-secondary mb-6 opacity-60" data-id="element-4185">
                  Protect your business interests and ensure smooth succession
                  planning for your company.
                </p>
                <ul className="space-y-3 mb-8 flex-1 opacity-60" data-id="element-4186">
                  <li className="flex items-start gap-2.5 text-sm text-secondary" data-id="element-4187">
                    <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" data-id="element-4188" />
                    <span data-id="element-4189">Business succession planning</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-secondary" data-id="element-4190">
                    <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" data-id="element-4191" />
                    <span data-id="element-4192">Partner buyout provisions</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-secondary" data-id="element-4193">
                    <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" data-id="element-4194" />
                    <span data-id="element-4195">Intellectual property transfer</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm text-secondary" data-id="element-4196">
                    <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" data-id="element-4197" />
                    <span data-id="element-4198">Key employee provisions</span>
                  </li>
                </ul>
                <button disabled className="w-full bg-aliceBlue text-secondary py-3.5 rounded-xl font-semibold border border-borderColor cursor-not-allowed flex items-center justify-center gap-2" data-id="element-4199">
                  Start Business Will
                  <ChevronRight className="w-4 h-4" data-id="element-4200" />
                </button>
              </div>
            </div>
          </div>

          {/* My Wills Section */}
          <div className="mb-12" data-id="element-4201">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 flex-wrap" data-id="element-4202">
              <div className="flex items-center gap-3" data-id="element-4203">
                <h2 className="text-xl font-bold text-primary" data-id="element-4204">My Wills</h2>
              </div>
              <div className="flex items-center gap-4 flex-wrap" data-id="element-4205">
                <div className="flex items-center gap-2 text-sm" data-id="element-4206">
                  <span className="text-secondary" data-id="element-4207">
                    {myWills.length} of{' '}
                    {subscriptionPlan.maxWills === 999 ? '∞' : subscriptionPlan.maxWills}{' '}
                    wills used
                  </span>
                  {subscriptionPlan.maxWills !== 999}
                </div>
              </div>
            </div>

            {myWills.length >= subscriptionPlan.maxWills && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-id="element-4208">
                <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4209" />
                <div data-id="element-4210">
                  <p className="text-sm font-medium text-amber-800" data-id="element-4211">
                    Will limit reached on your {subscriptionPlan.name} plan
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5" data-id="element-4212">
                    Upgrade your plan to create additional wills.{' '}
                    <button onClick={() => setShowUpgradeModal(true)} className="underline font-medium hover:text-amber-900" data-id="element-4213">
                      View plans
                    </button>
                  </p>
                </div>
              </div>}

            <div className="grid grid-cols-1 gap-3" data-id="element-4214">
              {myWills.map(will => <div key={will.id} className="relative bg-white rounded-2xl border border-borderColor shadow-sm hover:shadow-md hover:border-violetBlue/30 transition-all group" data-id="element-4215">
                  <AnimatePresence mode="wait" data-id="element-4216">
                    {confirmDeleteId === will.id ? <motion.div key="confirm" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} className="p-5 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-4217">
                        <div className="flex items-center gap-3" data-id="element-4218">
                          <div className="p-2 bg-destructive/10 rounded-xl flex-shrink-0" data-id="element-4219">
                            <Trash2 className="w-5 h-5 text-destructive" data-id="element-4220" />
                          </div>
                          <div data-id="element-4221">
                            <p className="text-sm font-semibold text-primary" data-id="element-4222">
                              Delete "{will.title}"?
                            </p>
                            <p className="text-xs text-secondary mt-0.5" data-id="element-4223">
                              This action cannot be undone.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-4224">
                          <button onClick={() => setConfirmDeleteId(null)} className="px-4 py-2 text-sm font-medium text-secondary border border-borderColor rounded-lg hover:bg-aliceBlue transition-colors" data-id="element-4225">
                            Cancel
                          </button>
                          <button onClick={() => handleDeleteWill(will.id)} className="px-4 py-2 text-sm font-medium text-white bg-destructive rounded-lg hover:bg-destructive/90 transition-colors" data-id="element-4226">
                            Delete
                          </button>
                        </div>
                      </motion.div> : <motion.div key="card" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} onClick={() => {
                  setActiveWillId(will.id);
                  setViewState('resume');
                }} className="p-5 cursor-pointer" data-id="element-4227">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4" data-id="element-4228">
                          <div className="flex items-center gap-3" data-id="element-4229">
                            <div className="p-2.5 bg-aliceBlue rounded-xl group-hover:bg-violetBlue/5 transition-colors flex-shrink-0" data-id="element-4230">
                              {will.type === 'personal' ? <User className="w-5 h-5 text-primary group-hover:text-violetBlue transition-colors" data-id="element-4231" /> : <Briefcase className="w-5 h-5 text-primary group-hover:text-violetBlue transition-colors" data-id="element-4232" />}
                            </div>
                            <div className="min-w-0" data-id="element-4233">
                              <h3 className="font-semibold text-sm text-primary group-hover:text-violetBlue transition-colors leading-tight" data-id="element-4234">
                                {will.title}
                              </h3>
                              <div className="flex items-center gap-2 text-xs text-secondary mt-1" data-id="element-4235">
                                <span className="flex items-center gap-1" data-id="element-4236">
                                  {will.type === 'personal' ? <User className="w-3 h-3" data-id="element-4237" /> : <Briefcase className="w-3 h-3" data-id="element-4238" />}
                                  {will.type === 'personal' ? 'Personal Will' : 'Business Will'}
                                </span>
                                <span className="text-disabled" data-id="element-4239">•</span>
                                <span className="flex items-center gap-1" data-id="element-4240">
                                  <Clock className="w-3 h-3" data-id="element-4241" />{' '}
                                  {will.lastEdited}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 sm:gap-4 sm:flex-shrink-0" data-id="element-4242">
                            <div className="relative w-10 h-10 flex-shrink-0" data-id="element-4243">
                              <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36" data-id="element-4244">
                                <circle cx="18" cy="18" r="15" fill="none" stroke="#E8EEF4" strokeWidth="3" data-id="element-4245" />
                                <circle cx="18" cy="18" r="15" fill="none" stroke={will.progress === 100 ? '#00DACE' : '#7854FF'} strokeWidth="3" strokeLinecap="round" strokeDasharray={`${will.progress * 0.9425} 94.25`} className="transition-all duration-300" data-id="element-4246" />
                              </svg>
                              <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold ${will.progress === 100 ? 'text-turquoise' : 'text-violetBlue'}`} data-id="element-4247">
                                {will.progress}%
                              </span>
                            </div>
                            {renderStatusBadge(will.status)}
                            <button onClick={e => {
                        e.stopPropagation();
                        setConfirmDeleteId(will.id);
                      }} className="p-2 rounded-lg text-disabled hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100" title="Delete will" data-id="element-4248">
                              <Trash2 className="w-4 h-4" data-id="element-4249" />
                            </button>
                            <ChevronRight className="w-5 h-5 text-disabled group-hover:text-violetBlue transition-colors hidden sm:block flex-shrink-0" data-id="element-4250" />
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}

              {myWills.length === 0 && <div className="bg-white rounded-2xl border border-borderColor shadow-sm p-8 sm:p-10" data-id="element-4251">
                  <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8" data-id="element-4252">
                    <div className="p-4 bg-violetBlue/5 rounded-2xl flex-shrink-0" data-id="element-4253">
                      <Shield className="w-12 h-12 text-violetBlue" data-id="element-4254" />
                    </div>
                    <div className="text-center sm:text-left flex-1" data-id="element-4255">
                      <h3 className="text-lg font-bold text-primary mb-1.5" data-id="element-4256">
                        You haven't created a Will yet
                      </h3>
                      <p className="text-sm text-secondary leading-relaxed mb-4 max-w-lg" data-id="element-4257">
                        Protect your family and assets in minutes. Our guided
                        builder walks you through every step with plain-language
                        questions — no legal jargon required.
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mb-5 justify-center sm:justify-start" data-id="element-4258">
                        <span className="flex items-center gap-1.5 text-xs text-secondary bg-aliceBlue px-2.5 py-1 rounded-full" data-id="element-4259">
                          <Clock className="w-3 h-3 text-violetBlue" data-id="element-4260" />
                          ~15 minutes
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-secondary bg-aliceBlue px-2.5 py-1 rounded-full" data-id="element-4261">
                          <Shield className="w-3 h-3 text-violetBlue" data-id="element-4262" />
                          Province-specific
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-secondary bg-aliceBlue px-2.5 py-1 rounded-full" data-id="element-4263">
                          <CheckCircle className="w-3 h-3 text-turquoise" data-id="element-4264" />
                          Legally compliant
                        </span>
                      </div>
                      <button onClick={() => setViewState('setup')} className="bg-violetBlue text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#6844E8] transition-colors inline-flex items-center gap-2" data-id="element-4265">
                        Create Your First Will
                        <ChevronRight className="w-4 h-4" data-id="element-4266" />
                      </button>
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* FAQ Section */}
          <div data-id="element-4267">
            <h2 className="text-xl font-bold text-primary mb-6 flex items-center gap-2" data-id="element-4268">
              <HelpCircle className="w-5 h-5 text-secondary" data-id="element-4269" />
              Frequently Asked Questions
            </h2>
            <div className="bg-white rounded-2xl border border-borderColor shadow-sm overflow-hidden" data-id="element-4270">
              {faqs.map((faq, index) => <div key={faq.id} className={`border-b border-borderColor last:border-0`} data-id="element-4271">
                  <button onClick={() => toggleFaq(faq.id)} className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-aliceBlue/50 transition-colors" data-id="element-4272">
                    <span className="font-semibold text-primary pr-4" data-id="element-4273">
                      {faq.question}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-secondary flex-shrink-0 transition-transform duration-200 ${expandedFaq === faq.id ? 'rotate-180' : ''}`} data-id="element-4274" />
                  </button>
                  <AnimatePresence data-id="element-4275">
                    {expandedFaq === faq.id && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-4276">
                        <div className="p-5 sm:p-6 pt-0 text-sm text-secondary leading-relaxed" data-id="element-4277">
                          {faq.answer}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>
        </div>
      </div>;
  };
  const [setupProvince, setSetupProvince] = useState(formData.province || '');
  const [setupLanguage, setSetupLanguage] = useState<'EN' | 'FR'>('EN');
  const canadianProvinces = ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'];
  const handleSetupComplete = () => {
    const newWillId = `will-${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      province: setupProvince
    }));
    setCurrentMilestoneIndex(0);
    setCurrentStepIndex(0);
    setMilestoneStatuses({
      about: 'in_progress',
      background: 'available',
      family: 'locked',
      executor: 'locked',
      funeral: 'locked',
      personalGifts: 'locked',
      specialAssets: 'locked',
      financialGifts: 'locked',
      realEstate: 'locked',
      review: 'locked'
    });
    setDraftStatus('in_progress');
    const newWill: WillEntry = {
      id: newWillId,
      title: `Last Will and Testament — ${formData.fullName || 'New Will'}`,
      type: 'personal',
      status: 'in_progress',
      progress: 0,
      lastEdited: 'Just now'
    };
    setMyWills(prev => [newWill, ...prev]);
    setActiveWillId(newWillId);
    setViewState('builder');
  };
  const renderSetupState = () => <div className="flex flex-col h-full bg-white overflow-y-auto" data-id="element-4278">
      <div className="px-4 sm:px-6 lg:pl-[100px] lg:pr-8 py-4 border-b border-borderColor" data-id="element-4279">
        <button onClick={() => setViewState('landing')} className="flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-4280">
          <ArrowLeft className="w-4 h-4" data-id="element-4281" />
          Back to Wills Hub
        </button>
      </div>
      <div className="flex-1 w-full" data-id="element-4282">
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-12 sm:py-20" data-id="element-4283">
          <div className="bg-white rounded-2xl border border-borderColor shadow-sm p-6 sm:p-10" data-id="element-4284">
            <div className="text-center mb-8" data-id="element-4285">
              <div className="inline-flex items-center justify-center p-3 bg-violetBlue/10 rounded-2xl mb-4" data-id="element-4286">
                <FileText className="w-8 h-8 text-violetBlue" data-id="element-4287" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary mb-2" data-id="element-4288">
                Let's create your Will
              </h1>
              <p className="text-sm text-secondary leading-relaxed max-w-md mx-auto" data-id="element-4289">
                We'll guide you through every step. First, confirm a few details
                so your Will is legally accurate for your province.
              </p>
            </div>
            <div className="space-y-5 mb-8" data-id="element-4290">
              <div data-id="element-4291">
                <label className="block text-sm font-medium text-primary mb-2" data-id="element-4292">
                  Province of residence
                </label>
                <select value={setupProvince} onChange={e => setSetupProvince(e.target.value)} className="w-full px-4 py-3 border border-borderColor rounded-xl text-sm text-primary focus:outline-none focus:border-violetBlue focus:ring-2 focus:ring-violetBlue/20 transition-all appearance-none bg-white" data-id="element-4293">
                  <option value="" data-id="element-4294">Select your province</option>
                  {canadianProvinces.map(p => <option key={p} value={p} data-id="element-4295">
                      {p}
                    </option>)}
                </select>
                <p className="text-xs text-secondary mt-1.5" data-id="element-4296">
                  Will requirements vary by province. We'll tailor the process
                  accordingly.
                </p>
              </div>
              <div data-id="element-4297">
                <label className="block text-sm font-medium text-primary mb-2" data-id="element-4298">
                  Preferred language
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-4299">
                  <button type="button" onClick={() => setSetupLanguage('EN')} className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${setupLanguage === 'EN' ? 'border-violetBlue bg-violetBlue/5 text-primary ring-2 ring-violetBlue/20' : 'border-borderColor text-secondary hover:border-violetBlue/40 hover:bg-aliceBlue'}`} aria-pressed={setupLanguage === 'EN'} data-id="element-4300">
                    <span className="text-2xl leading-none" aria-hidden="true" data-id="element-4301">
                      🇨🇦
                    </span>
                    <span className="flex flex-col items-start" data-id="element-4302">
                      <span className="font-semibold" data-id="element-4303">English</span>
                      <span className="text-xs text-secondary" data-id="element-4304">Canada</span>
                    </span>
                  </button>
                  <button type="button" onClick={() => setSetupLanguage('FR')} className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${setupLanguage === 'FR' ? 'border-violetBlue bg-violetBlue/5 text-primary ring-2 ring-violetBlue/20' : 'border-borderColor text-secondary hover:border-violetBlue/40 hover:bg-aliceBlue'}`} aria-pressed={setupLanguage === 'FR'} data-id="element-4305">
                    <img src="https://cdn.magicpatterns.com/uploads/xdFtCHVkCvteuQG3Jsqi5s/quebec-flag.png" alt="" aria-hidden="true" className="w-7 h-5 object-cover rounded-sm border border-borderColor flex-shrink-0" data-id="element-4306" />
                    <span className="flex flex-col items-start" data-id="element-4307">
                      <span className="font-semibold" data-id="element-4308">Français</span>
                      <span className="text-xs text-secondary" data-id="element-4309">Québec</span>
                    </span>
                  </button>
                </div>
                <p className="text-xs text-secondary mt-1.5" data-id="element-4310">
                  Your Will will be drafted in this language.
                </p>
              </div>
            </div>
            <button onClick={handleSetupComplete} disabled={!setupProvince} className={`w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${setupProvince ? 'bg-violetBlue text-white hover:bg-[#6844E8]' : 'bg-disabled/30 text-disabled cursor-not-allowed'}`} data-id="element-4311">
              Begin Your Will <ChevronRight className="w-4 h-4" data-id="element-4312" />
            </button>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-6 pt-6 border-t border-borderColor" data-id="element-4313">
              <span className="flex items-center gap-1.5 text-xs text-secondary" data-id="element-4314">
                <Clock className="w-3.5 h-3.5 text-violetBlue" data-id="element-4315" /> ~15 min to
                complete
              </span>
              <span className="flex items-center gap-1.5 text-xs text-secondary" data-id="element-4316">
                <Shield className="w-3.5 h-3.5 text-violetBlue" data-id="element-4317" /> Save & resume
                anytime
              </span>
              <span className="flex items-center gap-1.5 text-xs text-secondary" data-id="element-4318">
                <CheckCircle className="w-3.5 h-3.5 text-turquoise" data-id="element-4319" /> Legally
                compliant
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
  useEffect(() => {
    if (viewState === 'builder') {
      setSaveStatus('saving');
      const timer = setTimeout(() => {
        setSaveStatus('saved');
        setLastSaved(new Date());
        setLastSavedMinutesAgo(0);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [formData, currentStepIndex, currentMilestoneIndex, viewState]);
  useEffect(() => {
    if (saveStatus === 'saved' || saveStatus === 'saved_ago') {
      const interval = setInterval(() => {
        const mins = Math.floor((new Date().getTime() - lastSaved.getTime()) / 60000);
        setLastSavedMinutesAgo(mins);
        if (mins > 0 && saveStatus !== 'saved_ago') {
          setSaveStatus('saved_ago');
        }
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [lastSaved, saveStatus]);
  const calculateReadiness = () => {
    let totalRequired = 9;
    let completed = 0;
    let flagged = 0;
    let skipped = 0;
    let conflicting = 0;
    let partialProgress = 0;
    if (milestoneStatuses.about === 'completed') completed++;
    if (milestoneStatuses.background === 'completed') completed++;
    if (milestoneStatuses.family === 'completed') completed++;
    if (milestoneStatuses.executor === 'completed') completed++;
    if (milestoneStatuses.executor === 'flagged') flagged++;
    if (milestoneStatuses.funeral === 'completed') completed++;
    if (milestoneStatuses.personalGifts === 'completed') completed++;
    if (milestoneStatuses.personalGifts === 'skipped') skipped++;
    if (milestoneStatuses.specialAssets === 'completed') completed++;
    if (milestoneStatuses.specialAssets === 'skipped') skipped++;
    if (milestoneStatuses.financialGifts === 'completed') completed++;
    if (milestoneStatuses.financialGifts === 'skipped') skipped++;
    if (formData.distributionType === 'split') {
      const totalPct = formData.beneficiaries.reduce((sum, b) => sum + b.percentage, 0);
      if (totalPct !== 100) conflicting++;
    }
    if (milestoneStatuses.realEstate === 'completed') completed++;
    if (milestoneStatuses.realEstate === 'skipped') skipped++;
    if (viewState === 'builder' || viewState === 'resume') {
      const currentMilestone = MILESTONES[currentMilestoneIndex];
      if (currentMilestone && currentMilestone.id !== 'review' && milestoneStatuses[currentMilestone.id] === 'in_progress' && currentMilestone.questions > 0) {
        partialProgress = currentStepIndex / currentMilestone.questions;
      }
    }
    const percentage = Math.round((completed + skipped + partialProgress) / totalRequired * 100);
    return {
      percentage,
      completed,
      flagged,
      skipped,
      conflicting,
      isReady: percentage === 100 && flagged === 0 && conflicting === 0,
      needsReview: flagged > 0 || conflicting > 0
    };
  };
  const readiness = calculateReadiness();
  useEffect(() => {
    if (viewState === 'resume') {
      if (readiness.conflicting > 0 || readiness.flagged > 0) {
        setDraftStatus('needs_review');
      } else if (readiness.isReady) {
        setDraftStatus('completed');
      } else {
        setDraftStatus('in_progress');
      }
    }
  }, [readiness.isReady, readiness.conflicting, readiness.flagged, viewState]);
  useEffect(() => {
    if (!activeWillId) return;
    setMyWills(prev => prev.map(w => w.id === activeWillId ? {
      ...w,
      progress: readiness.percentage,
      status: draftStatus,
      lastEdited: viewState === 'builder' || viewState === 'resume' || viewState === 'review' ? 'Just now' : w.lastEdited
    } : w));
  }, [readiness.percentage, draftStatus, activeWillId, viewState]);
  const handleNext = () => {
    const currentMilestone = MILESTONES[currentMilestoneIndex];
    let canProceed = true;
    let skipToNextMilestone = false;
    let skipSteps = 0;
    // Soft validation: show warning when continuing without executor, but don't block
    if (currentMilestone.id === 'executor' && currentStepIndex === 0 && !formData.executor) {
      if (!showExecutorWarning) {
        setShowExecutorWarning(true);
        return; // First press shows warning, second press proceeds
      }
      // Second press — reset warning and continue
      setShowExecutorWarning(false);
    } else {
      setShowExecutorWarning(false);
    }
    if (currentMilestone.id === 'family' && currentStepIndex === 1 && !formData.hasChildren) skipToNextMilestone = true;
    if (currentMilestone.id === 'funeral' && currentStepIndex === 0) {
      if (formData.funeralArrangement === 'leave_to_executor') skipToNextMilestone = true;else if (formData.funeralArrangement === 'burial') skipSteps = 1;
    }
    if (currentMilestone.id === 'personalGifts' && currentStepIndex === 0 && !formData.hasPersonalProperty) skipToNextMilestone = true;
    if (currentMilestone.id === 'specialAssets' && currentStepIndex === 0 && !formData.hasArtCollectibles) skipToNextMilestone = true;
    if (currentMilestone.id === 'financialGifts') {
      if (currentStepIndex === 0 && !formData.hasFinancialGifts) skipSteps = 1;
      if (currentStepIndex === 2 && !formData.hasCharityGifts) skipSteps = 1;
      if (currentStepIndex === 4 && formData.distributionType === 'split') {
        const totalPct = formData.beneficiaries.reduce((sum, b) => sum + b.percentage, 0);
        if (totalPct !== 100) {
          setMilestoneStatuses(prev => ({
            ...prev,
            financialGifts: 'flagged'
          }));
        }
      }
    }
    if (currentMilestone.id === 'realEstate' && currentStepIndex === 0 && !formData.hasRealEstateGifts) skipToNextMilestone = true;
    if (!canProceed) return;
    setRecentlyUpdatedSection(currentMilestone.id);
    setShowDraftUpdatedNotification(true);
    setTimeout(() => {
      setShowDraftUpdatedNotification(false);
      setRecentlyUpdatedSection(null);
    }, 2000);
    if (skipToNextMilestone || currentStepIndex + skipSteps >= currentMilestone.questions - 1) {
      const newStatuses = {
        ...milestoneStatuses
      };
      if (skipToNextMilestone && currentStepIndex === 0) {
        newStatuses[currentMilestone.id] = 'skipped';
      } else if (newStatuses[currentMilestone.id] !== 'flagged') {
        newStatuses[currentMilestone.id] = 'completed';
      }
      if (currentMilestone.id === 'executor' && !formData.executor) newStatuses.executor = 'flagged';
      setMilestoneStatuses(newStatuses);
      setShowCompletionFeedback(true);
      setTimeout(() => {
        setShowCompletionFeedback(false);
        let nextIndex = currentMilestoneIndex + 1;
        if (nextIndex >= MILESTONES.length - 1) {
          setViewState('review');
        } else {
          setMilestoneStatuses(prev => ({
            ...prev,
            [MILESTONES[nextIndex].id]: 'in_progress'
          }));
          setCurrentMilestoneIndex(nextIndex);
          setCurrentStepIndex(0);
        }
      }, 2000);
    } else {
      setCurrentStepIndex(prev => prev + 1 + skipSteps);
    }
  };
  const handleBack = () => {
    if (currentStepIndex > 0) {
      let skipSteps = 1;
      const currentMilestone = MILESTONES[currentMilestoneIndex];
      if (currentMilestone.id === 'funeral' && currentStepIndex === 2 && formData.funeralArrangement === 'burial') skipSteps = 2;
      if (currentMilestone.id === 'financialGifts') {
        if (currentStepIndex === 2 && !formData.hasFinancialGifts) skipSteps = 2;
        if (currentStepIndex === 4 && !formData.hasCharityGifts) skipSteps = 2;
      }
      setCurrentStepIndex(prev => prev - skipSteps);
    } else if (currentMilestoneIndex > 0) {
      let prevIndex = currentMilestoneIndex - 1;
      setCurrentMilestoneIndex(prevIndex);
      setCurrentStepIndex(MILESTONES[prevIndex].questions - 1);
    }
  };
  const handleSaveAndExit = () => {
    console.log('Saving and exiting...');
    setViewState('landing');
  };
  const handleNavigateToAICopilot = () => {
    if (onNavigateToAICopilot) onNavigateToAICopilot();else navigate('/ai-copilot');
  };
  const handleNavigate = (path: string) => {
    if (onNavigate) onNavigate(path);else navigate(path);
  };
  const handleMenuClick = () => {
    if (onMenuClick) onMenuClick();else toggleMobileSidebar();
  };
  const toggleReviewSection = (id: string) => {
    setExpandedReviewSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const navigateToEdit = (milestoneIndex: number) => {
    setCurrentMilestoneIndex(milestoneIndex);
    setCurrentStepIndex(0);
    setViewState('builder');
    setIsMobilePreviewOpen(false);
  };
  const getLegalClauses = () => {
    return [{
      id: 'about',
      number: 1,
      title: 'Identification',
      status: milestoneStatuses.about,
      isRecentlyUpdated: recentlyUpdatedSection === 'about',
      onEdit: () => navigateToEdit(0),
      content: <p data-id="element-4320">
            I, <strong data-id="element-4321">{formData.fullName.toUpperCase()}</strong>, of the City
            of {formData.cityOfBirth || '[CITY]'}, in the Province of{' '}
            {formData.province}, declare this to be my Last Will and Testament.
          </p>
    }, {
      id: 'family',
      number: 2,
      title: 'Family Declaration',
      status: milestoneStatuses.family,
      isRecentlyUpdated: recentlyUpdatedSection === 'family',
      onEdit: () => navigateToEdit(2),
      content: <p data-id="element-4322">
            {formData.maritalStatus === 'married' ? `I declare that I am married to ${formData.spouseLegalName}, and that ` : `I declare that I am ${formData.maritalStatus || 'single'}, and that `}
            {formData.hasChildren ? `I have ${formData.children.length} living children.` : 'I have no children.'}
          </p>
    }, {
      id: 'executor',
      number: 3,
      title: 'Appointment of Executor',
      status: milestoneStatuses.executor,
      isRecentlyUpdated: recentlyUpdatedSection === 'executor',
      onEdit: () => navigateToEdit(3),
      content: <>
            <p data-id="element-4323">
              I appoint{' '}
              <strong data-id="element-4324">
                {formData.executor ? formData.executor.split(' (')[0].toUpperCase() : '[EXECUTOR]'}
              </strong>{' '}
              to be the Executor and Trustee of this my Will.
            </p>
            {formData.alternateExecutors.length > 0 && <p className="mt-4" data-id="element-4325">
                If{' '}
                <strong data-id="element-4326">
                  {formData.executor.split(' (')[0].toUpperCase()}
                </strong>{' '}
                is unwilling or unable to act, I appoint{' '}
                <strong data-id="element-4327">
                  {formData.alternateExecutors[0].legalName.toUpperCase()}
                </strong>{' '}
                to act in their place.
              </p>}
          </>
    }, {
      id: 'funeral',
      number: 4,
      title: 'Funeral Wishes',
      status: milestoneStatuses.funeral,
      isRecentlyUpdated: recentlyUpdatedSection === 'funeral',
      onEdit: () => navigateToEdit(4),
      content: <p data-id="element-4328">
            {formData.funeralArrangement === 'leave_to_executor' ? 'I leave all decisions regarding my funeral arrangements to my Executor.' : `I direct that my remains be ${formData.funeralArrangement === 'cremation' ? 'cremated' : 'buried'}.`}
            {formData.funeralArrangement === 'cremation' && formData.ashesInstructions && ` I direct that my ashes be ${formData.ashesInstructions}.`}
            {formData.specialFuneralInstructions && ` Furthermore, I request: ${formData.specialFuneralInstructions}`}
          </p>
    }, {
      id: 'personalGifts',
      number: 5,
      title: 'Specific Gifts of Personal Property',
      status: milestoneStatuses.personalGifts,
      isRecentlyUpdated: recentlyUpdatedSection === 'personalGifts',
      onEdit: () => navigateToEdit(5),
      content: <p data-id="element-4329">
            {formData.hasPersonalProperty ? `I direct my Trustee to distribute the following specific gifts: ${formData.personalPropertyGifts.map(g => `${g.quantity} ${g.description} to ${g.recipientLegalName}`).join(', ')}` : 'I make no specific gifts of personal property.'}
          </p>
    }, {
      id: 'financialGifts',
      number: 6,
      title: 'Distribution of Residue',
      status: milestoneStatuses.financialGifts,
      isRecentlyUpdated: recentlyUpdatedSection === 'financialGifts',
      onEdit: () => navigateToEdit(7),
      content: <p data-id="element-4330">
            {formData.distributionType === 'split' ? 'I direct my Trustee to divide the residue of my estate into shares and distribute them as follows: ' + formData.beneficiaries.map(b => `${b.percentage}% to ${b.name}`).join(', ') + '.' : 'I direct my trustee to transfer the residue of my estate to my spouse, if they survive me. If they do not survive me, then to my children in equal shares.'}
          </p>
    }, {
      id: 'realEstate',
      number: 7,
      title: 'Real Property',
      status: milestoneStatuses.realEstate,
      isRecentlyUpdated: recentlyUpdatedSection === 'realEstate',
      onEdit: () => navigateToEdit(8),
      content: <p data-id="element-4331">
            {formData.hasRealEstateGifts ? `I direct my Trustee to transfer the following real properties: ${formData.realEstateGifts.map(r => `${r.propertyAddress} to ${r.recipientLegalName}`).join('; ')}.` : 'I make no specific gifts of real property.'}
          </p>
    }];
  };
  const renderResumeState = () => <div className="flex flex-col h-full bg-white" data-id="element-4332">
      <div className="px-4 sm:px-6 lg:pl-[100px] lg:pr-8 py-4 border-b border-borderColor" data-id="element-4333">
        <button onClick={() => setViewState('landing')} className="flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-4334">
          <ArrowLeft className="w-4 h-4" data-id="element-4335" />
          Back to Wills Hub
        </button>
      </div>
      <ResumeDraftCard fullName={formData.fullName} draftStatus={draftStatus} readiness={readiness} lastSaved={lastSaved} milestones={MILESTONES} milestoneStatuses={milestoneStatuses} onContinue={() => {
      const firstIncomplete = MILESTONES.findIndex(m => milestoneStatuses[m.id] === 'in_progress' || milestoneStatuses[m.id] === 'available' || milestoneStatuses[m.id] === 'locked' || milestoneStatuses[m.id] === 'flagged');
      if (firstIncomplete !== -1) {
        setCurrentMilestoneIndex(firstIncomplete);
        setCurrentStepIndex(0);
      }
      setViewState('builder');
    }} onReview={() => setViewState('review')} showSaveToast={showSaveToast} data-id="element-4336" />
    </div>;
  const renderBuilderState = () => {
    const currentMilestone = MILESTONES[currentMilestoneIndex];
    if (!currentMilestone || currentMilestone.id === 'review' || currentMilestone.questions === 0) {
      setViewState('review');
      return null;
    }
    let continueDisabled = false;
    if (currentMilestone.id === 'about') {
      if (currentStepIndex === 0 && !formData.fullName) continueDisabled = true;
      if (currentStepIndex === 1 && (!formData.dob || !formData.province)) continueDisabled = true;
      if (currentStepIndex === 2 && !formData.address) continueDisabled = true;
    }
    if (currentMilestone.id === 'background') {
      if (currentStepIndex === 0 && (!formData.citizenship || !formData.immigrationStatus)) continueDisabled = true;
      if (currentStepIndex === 1 && (!formData.cityOfBirth || !formData.countryOfBirth)) continueDisabled = true;
      if (currentStepIndex === 2 && (!formData.occupation || !formData.employer)) continueDisabled = true;
      if (currentStepIndex === 3 && (!formData.businessAddress || !formData.businessPhone)) continueDisabled = true;
    }
    if (currentMilestone.id === 'family') {
      if (currentStepIndex === 0 && !formData.maritalStatus) continueDisabled = true;
      if (currentStepIndex === 0 && formData.maritalStatus === 'married' && !formData.spouseLegalName) continueDisabled = true;
      if (currentStepIndex === 1 && formData.hasChildren === null) continueDisabled = true;
    }
    if (currentMilestone.id === 'executor') {
      // Executor field is optional — users can skip and it shows as incomplete
    }
    if (currentMilestone.id === 'funeral') {
      if (currentStepIndex === 0 && !formData.funeralArrangement) continueDisabled = true;
      if (currentStepIndex === 1 && formData.funeralArrangement === 'cremation' && !formData.ashesInstructions) continueDisabled = true;
    }
    if (currentMilestone.id === 'personalGifts') {
      if (currentStepIndex === 0 && formData.hasPersonalProperty === null) continueDisabled = true;
    }
    if (currentMilestone.id === 'specialAssets') {
      if (currentStepIndex === 0 && formData.hasArtCollectibles === null) continueDisabled = true;
    }
    if (currentMilestone.id === 'financialGifts') {
      if (currentStepIndex === 0 && formData.hasFinancialGifts === null) continueDisabled = true;
      if (currentStepIndex === 2 && formData.hasCharityGifts === null) continueDisabled = true;
      if (currentStepIndex === 4 && !formData.distributionType) continueDisabled = true;
    }
    if (currentMilestone.id === 'realEstate') {
      if (currentStepIndex === 0 && formData.hasRealEstateGifts === null) continueDisabled = true;
    }
    return <div className="flex flex-col flex-1 min-h-0" data-id="element-4337">
        <div className="bg-white border-b border-borderColor flex-shrink-0 z-10 flex flex-col" data-id="element-4338">
          <div className="px-4 sm:px-6 py-2.5 sm:py-3 border-b border-borderColor/50" data-id="element-4339">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-3" data-id="element-4340">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-id="element-4341">
                <button onClick={() => setViewState('resume')} className="p-2 hover:bg-aliceBlue rounded-lg text-secondary transition-colors flex-shrink-0" aria-label="Back to draft overview" data-id="element-4342">
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-4343" />
                </button>
                {isRenamingDoc ? <div className="flex items-center gap-1.5 min-w-0" data-id="element-4344">
                    <input type="text" value={draftTitle} onChange={e => setDraftTitle(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (draftTitle.trim()) {
                      setDocumentTitle(draftTitle.trim());
                    } else {
                      setDraftTitle(documentTitle);
                    }
                    setIsRenamingDoc(false);
                  } else if (e.key === 'Escape') {
                    setDraftTitle(documentTitle);
                    setIsRenamingDoc(false);
                  }
                }} autoFocus className="px-2 py-1 text-sm sm:text-base font-semibold text-primary border border-violetBlue rounded-md focus:outline-none focus:ring-2 focus:ring-violetBlue/20 min-w-0 w-full max-w-[260px]" data-id="element-4345" />
                    <button onClick={() => {
                  if (draftTitle.trim()) {
                    setDocumentTitle(draftTitle.trim());
                  } else {
                    setDraftTitle(documentTitle);
                  }
                  setIsRenamingDoc(false);
                }} className="p-1.5 rounded-md text-turquoise hover:bg-turquoise/10 transition-colors flex-shrink-0" aria-label="Save name" data-id="element-4346">
                      <Check className="w-4 h-4" data-id="element-4347" />
                    </button>
                    <button onClick={() => {
                  setDraftTitle(documentTitle);
                  setIsRenamingDoc(false);
                }} className="p-1.5 rounded-md text-secondary hover:bg-aliceBlue transition-colors flex-shrink-0" aria-label="Cancel rename" data-id="element-4348">
                      <XCircle className="w-4 h-4" data-id="element-4349" />
                    </button>
                  </div> : <div className="flex items-center gap-1.5 min-w-0" data-id="element-4350">
                    <h2 className="font-semibold text-primary text-sm sm:text-base truncate" data-id="element-4351">
                      {documentTitle}
                    </h2>
                    <button onClick={() => {
                  setDraftTitle(documentTitle);
                  setIsRenamingDoc(true);
                }} className="p-1.5 rounded-md text-disabled hover:text-violetBlue hover:bg-violetBlue/5 transition-colors flex-shrink-0" aria-label="Rename document" title="Rename" data-id="element-4352">
                      <Pencil className="w-3.5 h-3.5" data-id="element-4353" />
                    </button>
                  </div>}
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-id="element-4354">
                {/* Secondary actions */}
                <div className="hidden md:flex items-center gap-1" data-id="element-4355">
                  <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors" title="Sign document" data-id="element-4356">
                    <PenLine className="w-4 h-4" data-id="element-4357" />
                    <span data-id="element-4358">Sign</span>
                  </button>
                  <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors" title="Share document" data-id="element-4359">
                    <Share2 className="w-4 h-4" data-id="element-4360" />
                    <span data-id="element-4361">Share</span>
                  </button>
                  <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors" title="Request a legal review" data-id="element-4362">
                    <FileCheck className="w-4 h-4" data-id="element-4363" />
                    <span className="hidden lg:inline" data-id="element-4364">Request a Review</span>
                    <span className="lg:hidden" data-id="element-4365">Review</span>
                  </button>
                  <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors" title="Download document" data-id="element-4366">
                    <Download className="w-4 h-4" data-id="element-4367" />
                    <span data-id="element-4368">Download</span>
                  </button>
                </div>
                {/* Autosave hint + Primary CTA grouped together */}
                <div className="flex items-center gap-2 sm:gap-2.5 pl-2 sm:pl-3 border-l border-borderColor" data-id="element-4369">
                  <div className="hidden sm:block" data-id="element-4370">
                    <AutosaveIndicator status={saveStatus} minutesAgo={lastSavedMinutesAgo} data-id="element-4371" />
                  </div>
                  <button onClick={handleSaveAndExit} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold bg-violetBlue text-white hover:bg-[#6844E8] transition-colors" aria-label="Save and exit" title="Save and exit" data-id="element-4372">
                    <Save className="w-4 h-4" data-id="element-4373" />
                    <span data-id="element-4374">Save &amp; Exit</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white border-t border-[0.5px] border-borderColor" data-id="element-4375">
            <PhaseStrip currentMilestoneId={MILESTONES[currentMilestoneIndex].id} milestoneStatuses={milestoneStatuses} overallPercentage={readiness.percentage} data-id="element-4376" />
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row max-w-7xl mx-auto w-full relative" data-id="element-4377">
          <div className="w-full md:w-1/2 lg:w-5/12 h-full overflow-y-auto bg-white p-4 sm:p-6 md:p-10 flex flex-col relative" data-id="element-4378">
            <AnimatePresence mode="wait" data-id="element-4379">
              {showCompletionFeedback ? <motion.div key="feedback" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 1.05
            }} className="flex-1 flex flex-col items-center justify-center text-center" data-id="element-4380">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-turquoise/10 rounded-full flex items-center justify-center mb-4 sm:mb-6" data-id="element-4381">
                    <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-turquoise" data-id="element-4382" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-primary mb-2" data-id="element-4383">
                    Done — one less thing to worry about.
                  </h3>
                  <p className="text-sm sm:text-base lg:text-lg text-secondary" data-id="element-4384">
                    Your {MILESTONES[currentMilestoneIndex].label} section is
                    complete. Let's keep going.
                  </p>
                </motion.div> : <Fragment key={`step-${currentMilestoneIndex}-${currentStepIndex}`} data-id="element-4385">
                  {currentMilestoneIndex === 0 && currentStepIndex === 0 && <QuestionCard sectionLabel="About You" question="What's your full legal name?" helperText="Please use your full legal name exactly as it appears on your government ID." required data-id="element-4386">
                      <input type="text" value={formData.fullName} onChange={e => setFormData({
                  ...formData,
                  fullName: e.target.value
                })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue focus:ring-2 focus:ring-violetBlue/20 outline-none text-base sm:text-lg text-primary" placeholder="e.g. John Michael Doe" data-id="element-4387" />
                    </QuestionCard>}
                  {currentMilestoneIndex === 0 && currentStepIndex === 1 && <QuestionCard sectionLabel="About You" question="When were you born, and where do you live?" required data-id="element-4388">
                      <div className="space-y-4" data-id="element-4389">
                        <div data-id="element-4390">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4391">
                            Date of Birth
                          </label>
                          <input type="date" value={formData.dob} onChange={e => setFormData({
                      ...formData,
                      dob: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue focus:ring-2 focus:ring-violetBlue/20 outline-none text-base sm:text-lg text-primary" data-id="element-4392" />
                        </div>
                        <div data-id="element-4393">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4394">
                            Province of Residence
                          </label>
                          <select value={formData.province} onChange={e => setFormData({
                      ...formData,
                      province: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue focus:ring-2 focus:ring-violetBlue/20 outline-none text-base sm:text-lg text-primary bg-white" data-id="element-4395">
                            <option value="" data-id="element-4396">Select a province</option>
                            <option value="Ontario" data-id="element-4397">Ontario</option>
                            <option value="British Columbia" data-id="element-4398">
                              British Columbia
                            </option>
                            <option value="Alberta" data-id="element-4399">Alberta</option>
                            <option value="Quebec" data-id="element-4400">Quebec</option>
                            <option value="Manitoba" data-id="element-4401">Manitoba</option>
                            <option value="Saskatchewan" data-id="element-4402">Saskatchewan</option>
                            <option value="Nova Scotia" data-id="element-4403">Nova Scotia</option>
                            <option value="New Brunswick" data-id="element-4404">New Brunswick</option>
                            <option value="Newfoundland and Labrador" data-id="element-4405">
                              Newfoundland and Labrador
                            </option>
                            <option value="Prince Edward Island" data-id="element-4406">
                              Prince Edward Island
                            </option>
                          </select>
                        </div>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 0 && currentStepIndex === 2 && <QuestionCard sectionLabel="About You" question="What's your current home address?" required data-id="element-4407">
                      <textarea value={formData.address} onChange={e => setFormData({
                  ...formData,
                  address: e.target.value
                })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue focus:ring-2 focus:ring-violetBlue/20 outline-none text-base sm:text-lg text-primary" rows={3} placeholder="e.g. 47 King Street West, Toronto, ON M5H 1J8" data-id="element-4408" />
                    </QuestionCard>}

                  {currentMilestoneIndex === 1 && currentStepIndex === 0 && <QuestionCard sectionLabel="Personal Background" question="What is your citizenship and immigration status?" required data-id="element-4409">
                      <div className="space-y-6" data-id="element-4410">
                        <div data-id="element-4411">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4412">
                            Country of Citizenship
                          </label>
                          <input type="text" value={formData.citizenship} onChange={e => setFormData({
                      ...formData,
                      citizenship: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. Canadian" data-id="element-4413" />
                        </div>
                        <div data-id="element-4414">
                          <label className="block text-sm font-medium text-secondary mb-3" data-id="element-4415">
                            Immigration Status
                          </label>
                          <select value={formData.immigrationStatus} onChange={e => setFormData({
                      ...formData,
                      immigrationStatus: e.target.value as any
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary bg-white" data-id="element-4416">
                            <option value="" data-id="element-4417">Select status</option>
                            <option value="citizen" data-id="element-4418">Canadian Citizen</option>
                            <option value="permanent_resident" data-id="element-4419">
                              Permanent Resident
                            </option>
                            <option value="temporary_resident" data-id="element-4420">
                              Temporary Resident
                            </option>
                            <option value="other" data-id="element-4421">Other</option>
                          </select>
                        </div>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 1 && currentStepIndex === 1 && <QuestionCard sectionLabel="Personal Background" question="Where were you born?" required data-id="element-4422">
                      <div className="space-y-4" data-id="element-4423">
                        <div data-id="element-4424">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4425">
                            City of Birth
                          </label>
                          <input type="text" value={formData.cityOfBirth} onChange={e => setFormData({
                      ...formData,
                      cityOfBirth: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. Toronto" data-id="element-4426" />
                        </div>
                        <div data-id="element-4427">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4428">
                            Country of Birth
                          </label>
                          <input type="text" value={formData.countryOfBirth} onChange={e => setFormData({
                      ...formData,
                      countryOfBirth: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. Canada" data-id="element-4429" />
                        </div>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 1 && currentStepIndex === 2 && <QuestionCard sectionLabel="Personal Background" question="What do you do for work?" required data-id="element-4430">
                      <div className="space-y-4" data-id="element-4431">
                        <div data-id="element-4432">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4433">
                            Occupation
                          </label>
                          <input type="text" value={formData.occupation} onChange={e => setFormData({
                      ...formData,
                      occupation: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. Software Engineer" data-id="element-4434" />
                        </div>
                        <div data-id="element-4435">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4436">
                            Employer
                          </label>
                          <input type="text" value={formData.employer} onChange={e => setFormData({
                      ...formData,
                      employer: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. Maple Tech Solutions Inc." data-id="element-4437" />
                        </div>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 1 && currentStepIndex === 3 && <QuestionCard sectionLabel="Personal Background" question="What's your business contact info?" required data-id="element-4438">
                      <div className="space-y-4" data-id="element-4439">
                        <div data-id="element-4440">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4441">
                            Business Address
                          </label>
                          <input type="text" value={formData.businessAddress} onChange={e => setFormData({
                      ...formData,
                      businessAddress: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. 200 Bay Street" data-id="element-4442" />
                        </div>
                        <div data-id="element-4443">
                          <label className="block text-sm font-medium text-secondary mb-2" data-id="element-4444">
                            Business Phone
                          </label>
                          <input type="tel" value={formData.businessPhone} onChange={e => setFormData({
                      ...formData,
                      businessPhone: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. +1 (416) 555-0199" data-id="element-4445" />
                        </div>
                      </div>
                    </QuestionCard>}

                  {currentMilestoneIndex === 2 && currentStepIndex === 0 && <QuestionCard sectionLabel="Family & Marital" question="What's your marital status?" required data-id="element-4446">
                      <div className="space-y-4" data-id="element-4447">
                        <select value={formData.maritalStatus} onChange={e => setFormData({
                    ...formData,
                    maritalStatus: e.target.value as any
                  })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary bg-white" data-id="element-4448">
                          <option value="" data-id="element-4449">Select status</option>
                          <option value="single" data-id="element-4450">Single</option>
                          <option value="married" data-id="element-4451">Married</option>
                          <option value="common-law" data-id="element-4452">Common-law</option>
                          <option value="divorced" data-id="element-4453">Divorced</option>
                          <option value="widowed" data-id="element-4454">Widowed</option>
                        </select>
                        {formData.maritalStatus === 'married' && <div data-id="element-4455">
                            <label className="block text-sm font-medium text-secondary mb-2 mt-4" data-id="element-4456">
                              Spouse's Full Legal Name
                            </label>
                            <input type="text" value={formData.spouseLegalName} onChange={e => setFormData({
                      ...formData,
                      spouseLegalName: e.target.value
                    })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. Jessica Anne Doe" data-id="element-4457" />
                          </div>}
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 2 && currentStepIndex === 1 && <QuestionCard sectionLabel="Family & Marital" question="Do you have any children?" required data-id="element-4458">
                      <div className="flex gap-4" data-id="element-4459">
                        <button onClick={() => setFormData({
                    ...formData,
                    hasChildren: true
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasChildren === true ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4460">
                          Yes
                        </button>
                        <button onClick={() => setFormData({
                    ...formData,
                    hasChildren: false
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasChildren === false ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4461">
                          No
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 2 && currentStepIndex === 2 && <QuestionCard sectionLabel="Family & Marital" question="Please list your children." required data-id="element-4462">
                      <div className="space-y-4" data-id="element-4463">
                        {formData.children.map((child, i) => <div key={child.id} className="flex gap-2" data-id="element-4464">
                            <input type="text" value={child.name} onChange={e => {
                      const newChildren = [...formData.children];
                      newChildren[i].name = e.target.value;
                      setFormData({
                        ...formData,
                        children: newChildren
                      });
                    }} className="flex-1 p-3 rounded-xl border border-borderColor" placeholder="Child's Name" data-id="element-4465" />
                            <input type="text" value={child.age} onChange={e => {
                      const newChildren = [...formData.children];
                      newChildren[i].age = e.target.value;
                      setFormData({
                        ...formData,
                        children: newChildren
                      });
                    }} className="w-24 p-3 rounded-xl border border-borderColor" placeholder="Age" data-id="element-4466" />
                          </div>)}
                        <button onClick={() => setFormData({
                    ...formData,
                    children: [...formData.children, {
                      id: Date.now().toString(),
                      name: '',
                      age: ''
                    }]
                  })} className="text-violetBlue text-sm font-medium flex items-center gap-1" data-id="element-4467">
                          <Plus className="w-4 h-4" data-id="element-4468" /> Add Child
                        </button>
                      </div>
                    </QuestionCard>}

                  {currentMilestoneIndex === 3 && currentStepIndex === 0 && <QuestionCard sectionLabel="Executors" question="Who will be your primary executor?" required data-id="element-4469">
                      <div className="space-y-4" data-id="element-4470">
                        <input type="text" value={formData.executor} onChange={e => setFormData({
                    ...formData,
                    executor: e.target.value,
                    executorNotSure: false
                  })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" placeholder="e.g. Jane Doe" data-id="element-4471" />
                        <AnimatePresence data-id="element-4472">
                          {showExecutorWarning && !formData.executor && <motion.div initial={{
                      opacity: 0,
                      y: -4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -4
                    }} transition={{
                      duration: 0.2
                    }} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-4473">
                              <Shield className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4474" />
                              <p className="text-xs text-amber-800 leading-relaxed" data-id="element-4475">
                                Your Will can't be finalized without an
                                executor. You can continue and come back to this
                                anytime.
                              </p>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 3 && currentStepIndex === 1 && <QuestionCard sectionLabel="Executors" question="Would you like to name alternate executors?" data-id="element-4476">
                      <div className="space-y-4" data-id="element-4477">
                        {formData.alternateExecutors.map((alt, i) => <div key={alt.id} className="flex gap-2" data-id="element-4478">
                            <input type="text" value={alt.legalName} onChange={e => {
                      const newAlts = [...formData.alternateExecutors];
                      newAlts[i].legalName = e.target.value;
                      setFormData({
                        ...formData,
                        alternateExecutors: newAlts
                      });
                    }} className="flex-1 p-3 rounded-xl border border-borderColor" placeholder="Alternate Executor Name" data-id="element-4479" />
                          </div>)}
                        <button onClick={() => setFormData({
                    ...formData,
                    alternateExecutors: [...formData.alternateExecutors, {
                      id: Date.now().toString(),
                      relationship: '',
                      legalName: ''
                    }]
                  })} className="text-violetBlue text-sm font-medium flex items-center gap-1" data-id="element-4480">
                          <Plus className="w-4 h-4" data-id="element-4481" /> Add Alternate Executor
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 3 && currentStepIndex === 2 && <QuestionCard sectionLabel="Executors" question="Executor section complete." data-id="element-4482">
                      <p className="text-secondary" data-id="element-4483">
                        You have finished the executor section. Click continue.
                      </p>
                    </QuestionCard>}

                  {currentMilestoneIndex === 4 && currentStepIndex === 0 && <QuestionCard sectionLabel="Funeral Wishes" question="What are your funeral arrangements?" required data-id="element-4484">
                      <select value={formData.funeralArrangement} onChange={e => setFormData({
                  ...formData,
                  funeralArrangement: e.target.value as any
                })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary bg-white" data-id="element-4485">
                        <option value="" data-id="element-4486">Select arrangement</option>
                        <option value="burial" data-id="element-4487">Burial</option>
                        <option value="cremation" data-id="element-4488">Cremation</option>
                        <option value="leave_to_executor" data-id="element-4489">
                          Leave to Executor
                        </option>
                      </select>
                    </QuestionCard>}
                  {currentMilestoneIndex === 4 && currentStepIndex === 1 && <QuestionCard sectionLabel="Funeral Wishes" question="Any specific instructions for your ashes?" required={formData.funeralArrangement === 'cremation'} data-id="element-4490">
                      <textarea value={formData.ashesInstructions} onChange={e => setFormData({
                  ...formData,
                  ashesInstructions: e.target.value
                })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" rows={3} placeholder="e.g. Scattered in the ocean" data-id="element-4491" />
                    </QuestionCard>}
                  {currentMilestoneIndex === 4 && currentStepIndex === 2 && <QuestionCard sectionLabel="Funeral Wishes" question="Any other special funeral instructions?" data-id="element-4492">
                      <textarea value={formData.specialFuneralInstructions} onChange={e => setFormData({
                  ...formData,
                  specialFuneralInstructions: e.target.value
                })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary" rows={3} placeholder="e.g. I want a celebration of life instead of a traditional funeral." data-id="element-4493" />
                    </QuestionCard>}

                  {currentMilestoneIndex === 5 && currentStepIndex === 0 && <QuestionCard sectionLabel="Personal Gifts" question="Do you have any specific personal property gifts?" required data-id="element-4494">
                      <div className="flex gap-4" data-id="element-4495">
                        <button onClick={() => setFormData({
                    ...formData,
                    hasPersonalProperty: true
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasPersonalProperty === true ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4496">
                          Yes
                        </button>
                        <button onClick={() => setFormData({
                    ...formData,
                    hasPersonalProperty: false
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasPersonalProperty === false ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4497">
                          No
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 5 && currentStepIndex === 1 && <QuestionCard sectionLabel="Personal Gifts" question="List your personal property gifts." data-id="element-4498">
                      <div className="space-y-4" data-id="element-4499">
                        {formData.personalPropertyGifts.map((gift, i) => <div key={gift.id} className="p-4 border border-borderColor rounded-xl space-y-3" data-id="element-4500">
                            <input type="text" value={gift.description} onChange={e => {
                      const newGifts = [...formData.personalPropertyGifts];
                      newGifts[i].description = e.target.value;
                      setFormData({
                        ...formData,
                        personalPropertyGifts: newGifts
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Description (e.g. Gold Watch)" data-id="element-4501" />
                            <input type="text" value={gift.recipientLegalName} onChange={e => {
                      const newGifts = [...formData.personalPropertyGifts];
                      newGifts[i].recipientLegalName = e.target.value;
                      setFormData({
                        ...formData,
                        personalPropertyGifts: newGifts
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Recipient Name" data-id="element-4502" />
                          </div>)}
                        <button onClick={() => setFormData({
                    ...formData,
                    personalPropertyGifts: [...formData.personalPropertyGifts, {
                      id: Date.now().toString(),
                      category: 'other',
                      description: '',
                      quantity: '1',
                      recipientRelationship: '',
                      recipientLegalName: ''
                    }]
                  })} className="text-violetBlue text-sm font-medium flex items-center gap-1" data-id="element-4503">
                          <Plus className="w-4 h-4" data-id="element-4504" /> Add Gift
                        </button>
                      </div>
                    </QuestionCard>}

                  {currentMilestoneIndex === 6 && currentStepIndex === 0 && <QuestionCard sectionLabel="Special Assets" question="Do you have any art or collectibles?" required data-id="element-4505">
                      <div className="flex gap-4" data-id="element-4506">
                        <button onClick={() => setFormData({
                    ...formData,
                    hasArtCollectibles: true
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasArtCollectibles === true ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4507">
                          Yes
                        </button>
                        <button onClick={() => setFormData({
                    ...formData,
                    hasArtCollectibles: false
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasArtCollectibles === false ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4508">
                          No
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 6 && currentStepIndex === 1 && <QuestionCard sectionLabel="Special Assets" question="List your art and collectibles." data-id="element-4509">
                      <div className="space-y-4" data-id="element-4510">
                        {formData.artCollectibles.map((item, i) => <div key={item.id} className="p-4 border border-borderColor rounded-xl space-y-3" data-id="element-4511">
                            <input type="text" value={item.description} onChange={e => {
                      const newItems = [...formData.artCollectibles];
                      newItems[i].description = e.target.value;
                      setFormData({
                        ...formData,
                        artCollectibles: newItems
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Description" data-id="element-4512" />
                            <input type="text" value={item.recipientLegalName} onChange={e => {
                      const newItems = [...formData.artCollectibles];
                      newItems[i].recipientLegalName = e.target.value;
                      setFormData({
                        ...formData,
                        artCollectibles: newItems
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Recipient Name" data-id="element-4513" />
                          </div>)}
                        <button onClick={() => setFormData({
                    ...formData,
                    artCollectibles: [...formData.artCollectibles, {
                      id: Date.now().toString(),
                      description: '',
                      estimatedValue: '',
                      recipientRelationship: '',
                      recipientLegalName: ''
                    }]
                  })} className="text-violetBlue text-sm font-medium flex items-center gap-1" data-id="element-4514">
                          <Plus className="w-4 h-4" data-id="element-4515" /> Add Item
                        </button>
                      </div>
                    </QuestionCard>}

                  {currentMilestoneIndex === 7 && currentStepIndex === 0 && <QuestionCard sectionLabel="Financial & Charity" question="Do you have any specific financial gifts?" required data-id="element-4516">
                      <div className="flex gap-4" data-id="element-4517">
                        <button onClick={() => setFormData({
                    ...formData,
                    hasFinancialGifts: true
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasFinancialGifts === true ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4518">
                          Yes
                        </button>
                        <button onClick={() => setFormData({
                    ...formData,
                    hasFinancialGifts: false
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasFinancialGifts === false ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4519">
                          No
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 7 && currentStepIndex === 1 && <QuestionCard sectionLabel="Financial & Charity" question="List your financial gifts." data-id="element-4520">
                      <div className="space-y-4" data-id="element-4521">
                        {formData.financialGifts.map((gift, i) => <div key={gift.id} className="p-4 border border-borderColor rounded-xl space-y-3" data-id="element-4522">
                            <input type="text" value={gift.amount} onChange={e => {
                      const newGifts = [...formData.financialGifts];
                      newGifts[i].amount = e.target.value;
                      setFormData({
                        ...formData,
                        financialGifts: newGifts
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Amount (e.g. $10,000)" data-id="element-4523" />
                            <input type="text" value={gift.recipientLegalName} onChange={e => {
                      const newGifts = [...formData.financialGifts];
                      newGifts[i].recipientLegalName = e.target.value;
                      setFormData({
                        ...formData,
                        financialGifts: newGifts
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Recipient Name" data-id="element-4524" />
                          </div>)}
                        <button onClick={() => setFormData({
                    ...formData,
                    financialGifts: [...formData.financialGifts, {
                      id: Date.now().toString(),
                      type: 'lump_sum',
                      amount: '',
                      recipientRelationship: '',
                      recipientLegalName: ''
                    }]
                  })} className="text-violetBlue text-sm font-medium flex items-center gap-1" data-id="element-4525">
                          <Plus className="w-4 h-4" data-id="element-4526" /> Add Financial Gift
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 7 && currentStepIndex === 2 && <QuestionCard sectionLabel="Financial & Charity" question="Do you want to leave any gifts to charity?" required data-id="element-4527">
                      <div className="flex gap-4" data-id="element-4528">
                        <button onClick={() => setFormData({
                    ...formData,
                    hasCharityGifts: true
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasCharityGifts === true ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4529">
                          Yes
                        </button>
                        <button onClick={() => setFormData({
                    ...formData,
                    hasCharityGifts: false
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasCharityGifts === false ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4530">
                          No
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 7 && currentStepIndex === 3 && <QuestionCard sectionLabel="Financial & Charity" question="List your charity gifts." data-id="element-4531">
                      <div className="space-y-4" data-id="element-4532">
                        {formData.charityGifts.map((gift, i) => <div key={gift.id} className="p-4 border border-borderColor rounded-xl space-y-3" data-id="element-4533">
                            <input type="text" value={gift.charityName} onChange={e => {
                      const newGifts = [...formData.charityGifts];
                      newGifts[i].charityName = e.target.value;
                      setFormData({
                        ...formData,
                        charityGifts: newGifts
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Charity Name" data-id="element-4534" />
                            <input type="text" value={gift.amount} onChange={e => {
                      const newGifts = [...formData.charityGifts];
                      newGifts[i].amount = e.target.value;
                      setFormData({
                        ...formData,
                        charityGifts: newGifts
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Amount" data-id="element-4535" />
                          </div>)}
                        <button onClick={() => setFormData({
                    ...formData,
                    charityGifts: [...formData.charityGifts, {
                      id: Date.now().toString(),
                      charityName: '',
                      amount: '',
                      purpose: ''
                    }]
                  })} className="text-violetBlue text-sm font-medium flex items-center gap-1" data-id="element-4536">
                          <Plus className="w-4 h-4" data-id="element-4537" /> Add Charity Gift
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 7 && currentStepIndex === 4 && <QuestionCard sectionLabel="Financial & Charity" question="How do you want to distribute the rest of your estate?" required data-id="element-4538">
                      <div className="space-y-4" data-id="element-4539">
                        <select value={formData.distributionType} onChange={e => setFormData({
                    ...formData,
                    distributionType: e.target.value as any
                  })} className="w-full p-3 sm:p-4 rounded-xl border border-borderColor focus:border-violetBlue outline-none text-base sm:text-lg text-primary bg-white" data-id="element-4540">
                          <option value="" data-id="element-4541">Select distribution type</option>
                          <option value="spouse_first" data-id="element-4542">
                            Everything to spouse, then children
                          </option>
                          <option value="split" data-id="element-4543">
                            Split among beneficiaries
                          </option>
                          <option value="custom" data-id="element-4544">Custom distribution</option>
                        </select>
                        {formData.distributionType === 'split' && <div className="mt-4 space-y-4" data-id="element-4545">
                            <p className="text-sm font-medium text-secondary" data-id="element-4546">
                              Beneficiaries (must total 100%)
                            </p>
                            {formData.beneficiaries.map((b, i) => <div key={b.id} className="flex gap-2 items-center" data-id="element-4547">
                                <input type="text" value={b.name} onChange={e => {
                        const newB = [...formData.beneficiaries];
                        newB[i].name = e.target.value;
                        setFormData({
                          ...formData,
                          beneficiaries: newB
                        });
                      }} className="flex-1 p-2 rounded-lg border border-borderColor" placeholder="Name" data-id="element-4548" />
                                <input type="number" value={b.percentage} onChange={e => {
                        const newB = [...formData.beneficiaries];
                        newB[i].percentage = Number(e.target.value);
                        setFormData({
                          ...formData,
                          beneficiaries: newB
                        });
                      }} className="w-20 p-2 rounded-lg border border-borderColor" placeholder="%" data-id="element-4549" />
                                <span className="text-secondary" data-id="element-4550">%</span>
                              </div>)}
                            <button onClick={() => setFormData({
                      ...formData,
                      beneficiaries: [...formData.beneficiaries, {
                        id: Date.now().toString(),
                        name: '',
                        percentage: 0
                      }]
                    })} className="text-violetBlue text-sm font-medium flex items-center gap-1" data-id="element-4551">
                              <Plus className="w-4 h-4" data-id="element-4552" /> Add Beneficiary
                            </button>
                          </div>}
                      </div>
                    </QuestionCard>}

                  {currentMilestoneIndex === 8 && currentStepIndex === 0 && <QuestionCard sectionLabel="Real Estate" question="Do you want to gift any specific real estate?" required data-id="element-4553">
                      <div className="flex gap-4" data-id="element-4554">
                        <button onClick={() => setFormData({
                    ...formData,
                    hasRealEstateGifts: true
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasRealEstateGifts === true ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4555">
                          Yes
                        </button>
                        <button onClick={() => setFormData({
                    ...formData,
                    hasRealEstateGifts: false
                  })} className={`flex-1 p-4 rounded-xl border-2 ${formData.hasRealEstateGifts === false ? 'border-violetBlue bg-[#EEEDFE]' : 'border-borderColor bg-white'}`} data-id="element-4556">
                          No
                        </button>
                      </div>
                    </QuestionCard>}
                  {currentMilestoneIndex === 8 && currentStepIndex === 1 && <QuestionCard sectionLabel="Real Estate" question="List your real estate gifts." data-id="element-4557">
                      <div className="space-y-4" data-id="element-4558">
                        {formData.realEstateGifts.map((gift, i) => <div key={gift.id} className="p-4 border border-borderColor rounded-xl space-y-3" data-id="element-4559">
                            <input type="text" value={gift.propertyAddress} onChange={e => {
                      const newGifts = [...formData.realEstateGifts];
                      newGifts[i].propertyAddress = e.target.value;
                      setFormData({
                        ...formData,
                        realEstateGifts: newGifts
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Property Address" data-id="element-4560" />
                            <input type="text" value={gift.recipientLegalName} onChange={e => {
                      const newGifts = [...formData.realEstateGifts];
                      newGifts[i].recipientLegalName = e.target.value;
                      setFormData({
                        ...formData,
                        realEstateGifts: newGifts
                      });
                    }} className="w-full p-2 rounded-lg border border-borderColor" placeholder="Recipient Name" data-id="element-4561" />
                          </div>)}
                        <button onClick={() => setFormData({
                    ...formData,
                    realEstateGifts: [...formData.realEstateGifts, {
                      id: Date.now().toString(),
                      propertyAddress: '',
                      propertyType: '',
                      recipientRelationship: '',
                      recipientLegalName: ''
                    }]
                  })} className="text-violetBlue text-sm font-medium flex items-center gap-1" data-id="element-4562">
                          <Plus className="w-4 h-4" data-id="element-4563" /> Add Property
                        </button>
                      </div>
                    </QuestionCard>}
                </Fragment>}
            </AnimatePresence>
            {!showCompletionFeedback && <StickyNavFooter onBack={handleBack} onContinue={handleNext} continueDisabled={continueDisabled} data-id="element-4564" />}
          </div>

          <div className="hidden md:flex w-1/2 lg:w-7/12 bg-white border-l border-borderColor flex-col h-full relative" data-id="element-4565">
            <div className="p-3 sm:p-4 border-b border-borderColor flex justify-between items-center bg-aliceBlue/30" data-id="element-4566">
              <h3 className="text-sm sm:text-base font-semibold text-primary" data-id="element-4567">
                Will Summary
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-6 sm:p-8 lg:p-12 bg-white relative" data-id="element-4568">
              <AnimatePresence mode="wait" data-id="element-4569">
                <motion.div key="summary" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="max-w-xl mx-auto space-y-4 sm:space-y-6 pb-12" data-id="element-4570">
                  <div className="text-center mb-6 sm:mb-8" data-id="element-4571">
                    <p className="text-xs sm:text-sm text-secondary" data-id="element-4572">
                      A plain-English overview of your choices so far.
                    </p>
                  </div>
                  <SummaryCard title="Personal Details" icon={User} status={milestoneStatuses.about} isActive={currentMilestoneIndex === 0} isRecentlyUpdated={recentlyUpdatedSection === 'about'} data-id="element-4573">
                    <div data-id="element-4574">
                      <p className="text-[10px] sm:text-xs text-secondary uppercase tracking-wider font-semibold" data-id="element-4575">
                        Full Name
                      </p>
                      <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4576">
                        {formData.fullName}
                      </p>
                    </div>
                    <div data-id="element-4577">
                      <p className="text-[10px] sm:text-xs text-secondary uppercase tracking-wider font-semibold" data-id="element-4578">
                        Location
                      </p>
                      <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4579">
                        {formData.address}, {formData.province}
                      </p>
                    </div>
                  </SummaryCard>
                  <SummaryCard title="Personal Background" icon={Briefcase} status={milestoneStatuses.background} isActive={currentMilestoneIndex === 1} isRecentlyUpdated={recentlyUpdatedSection === 'background'} data-id="element-4580">
                    <div data-id="element-4581">
                      <p className="text-[10px] sm:text-xs text-secondary uppercase tracking-wider font-semibold" data-id="element-4582">
                        Citizenship
                      </p>
                      <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4583">
                        {formData.citizenship} ({formData.immigrationStatus})
                      </p>
                    </div>
                    <div data-id="element-4584">
                      <p className="text-[10px] sm:text-xs text-secondary uppercase tracking-wider font-semibold" data-id="element-4585">
                        Occupation
                      </p>
                      <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4586">
                        {formData.occupation} at {formData.employer}
                      </p>
                    </div>
                  </SummaryCard>
                  <SummaryCard title="Family & Marital" icon={Users} status={milestoneStatuses.family} isActive={currentMilestoneIndex === 2} isRecentlyUpdated={recentlyUpdatedSection === 'family'} data-id="element-4587">
                    <div data-id="element-4588">
                      <p className="text-[10px] sm:text-xs text-secondary uppercase tracking-wider font-semibold" data-id="element-4589">
                        Spouse
                      </p>
                      <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4590">
                        {formData.maritalStatus === 'married' ? formData.spouseLegalName : 'None'}
                      </p>
                    </div>
                    <div data-id="element-4591">
                      <p className="text-[10px] sm:text-xs text-secondary uppercase tracking-wider font-semibold" data-id="element-4592">
                        Children
                      </p>
                      <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4593">
                        {formData.hasChildren ? `${formData.children.length} children` : 'No children'}
                      </p>
                    </div>
                  </SummaryCard>
                  <SummaryCard title="Executors" icon={Shield} status={milestoneStatuses.executor} isActive={currentMilestoneIndex === 3} isRecentlyUpdated={recentlyUpdatedSection === 'executor'} data-id="element-4594">
                    {milestoneStatuses.executor === 'flagged' ? <div className="p-3 bg-amber-50 rounded-lg text-xs sm:text-sm text-amber-800 border border-amber-200" data-id="element-4595">
                        Incomplete — no executor selected yet.
                      </div> : <div data-id="element-4596">
                        <p className="text-[10px] sm:text-xs text-secondary uppercase tracking-wider font-semibold" data-id="element-4597">
                          Primary Executor
                        </p>
                        <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4598">
                          {formData.executor ? formData.executor.split(' (')[0] : 'Not selected'}
                        </p>
                      </div>}
                  </SummaryCard>
                  <SummaryCard title="Funeral Wishes" icon={Heart} status={milestoneStatuses.funeral} isActive={currentMilestoneIndex === 4} isRecentlyUpdated={recentlyUpdatedSection === 'funeral'} data-id="element-4599">
                    <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4600">
                      {formData.funeralArrangement === 'cremation' ? 'Cremation' : formData.funeralArrangement === 'burial' ? 'Burial' : formData.funeralArrangement === 'leave_to_executor' ? 'Left to executor' : 'Not decided'}
                    </p>
                  </SummaryCard>
                  <SummaryCard title="Personal Gifts" icon={Gift} status={milestoneStatuses.personalGifts} isActive={currentMilestoneIndex === 5} isRecentlyUpdated={recentlyUpdatedSection === 'personalGifts'} data-id="element-4601">
                    <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4602">
                      {formData.hasPersonalProperty ? `${formData.personalPropertyGifts.length} specific gifts` : 'No specific gifts'}
                    </p>
                  </SummaryCard>
                  <SummaryCard title="Special Assets" icon={Star} status={milestoneStatuses.specialAssets} isActive={currentMilestoneIndex === 6} isRecentlyUpdated={recentlyUpdatedSection === 'specialAssets'} data-id="element-4603">
                    <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4604">
                      {formData.hasArtCollectibles ? `${formData.artCollectibles.length} special assets` : 'No special assets'}
                    </p>
                  </SummaryCard>
                  <SummaryCard title="Financial & Charity" icon={DollarSign} status={milestoneStatuses.financialGifts} isActive={currentMilestoneIndex === 7} isRecentlyUpdated={recentlyUpdatedSection === 'financialGifts'} data-id="element-4605">
                    {formData.distributionType === 'split' ? <div data-id="element-4606">
                        <p className="text-[10px] sm:text-xs text-secondary uppercase tracking-wider font-semibold mb-2" data-id="element-4607">
                          Distribution
                        </p>
                        {formData.beneficiaries.map(b => <div key={b.id} className="flex justify-between text-xs sm:text-sm mb-1" data-id="element-4608">
                            <span className="text-primary" data-id="element-4609">{b.name}</span>
                            <span className="font-medium text-primary" data-id="element-4610">
                              {b.percentage}%
                            </span>
                          </div>)}
                      </div> : formData.distributionType === 'spouse_first' ? <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4611">
                        Everything to spouse, then children
                      </p> : null}
                  </SummaryCard>
                  <SummaryCard title="Real Estate" icon={Home} status={milestoneStatuses.realEstate} isActive={currentMilestoneIndex === 8} isRecentlyUpdated={recentlyUpdatedSection === 'realEstate'} data-id="element-4612">
                    <p className="text-sm sm:text-base text-primary font-medium" data-id="element-4613">
                      {formData.hasRealEstateGifts ? `${formData.realEstateGifts.length} properties gifted` : 'No real estate gifts'}
                    </p>
                  </SummaryCard>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="md:hidden absolute bottom-28 right-4 z-30" data-id="element-4614">
          <button onClick={() => setIsMobilePreviewOpen(true)} className="bg-white border border-borderColor shadow-lg text-primary p-3 rounded-full flex items-center justify-center" data-id="element-4615">
            <Eye className="w-5 h-5 text-violetBlue" data-id="element-4616" />
          </button>
        </div>

        <MobilePreviewSheet isOpen={isMobilePreviewOpen} onClose={() => setIsMobilePreviewOpen(false)} data-id="element-4617">
          <div className="p-4 bg-aliceBlue min-h-full" data-id="element-4618">
            <div className="bg-white p-6 shadow-sm border border-borderColor rounded-xl" data-id="element-4619">
              <LegalPreview testatorName={formData.fullName} province={formData.province} clauses={getLegalClauses()} isReady={readiness.isReady} data-id="element-4620" />
            </div>
          </div>
        </MobilePreviewSheet>
      </div>;
  };
  const renderReviewState = () => <div className="flex-1 overflow-y-auto bg-white" data-id="element-4621">
      <div className="max-w-6xl mt-6 sm:mt-8 p-4 sm:p-6 lg:pl-[100px] lg:pr-8 pb-24" data-id="element-4622">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4623">
          <div data-id="element-4624">
            <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-1 sm:mb-2" data-id="element-4625">
              Review Your Will
            </h1>
            <p className="text-sm sm:text-lg text-secondary" data-id="element-4626">
              Please review your choices carefully before finalizing your
              document.
            </p>
          </div>
          <button onClick={() => setViewState('landing')} className="px-4 py-2 border border-borderColor rounded-lg text-secondary hover:text-primary hover:bg-aliceBlue transition-colors text-sm sm:text-base w-full sm:w-auto" data-id="element-4627">
            Save & Exit
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-4628">
          <div className="lg:col-span-2 space-y-4" data-id="element-4629">
            {!readiness.isReady && <HelperBlock variant="warning" className="mb-6" data-id="element-4630">
                <span className="font-semibold block mb-1" data-id="element-4631">
                  Your Will is not ready to finalize
                </span>
                Please resolve the highlighted items below.
              </HelperBlock>}

            <div className="space-y-3 sm:space-y-4" data-id="element-4632">
              {[{
              id: 'about',
              title: 'Personal Information',
              icon: User,
              status: milestoneStatuses.about,
              content: `${formData.fullName}\n${formData.address}, ${formData.province}`,
              legalExcerpt: `I, ${formData.fullName.toUpperCase()}, currently residing in the Province of ${formData.province}, declare this to be my Last Will and Testament...`,
              editIdx: 0
            }, {
              id: 'background',
              title: 'Personal Background',
              icon: Briefcase,
              status: milestoneStatuses.background,
              content: `${formData.citizenship} (${formData.immigrationStatus})\n${formData.occupation} at ${formData.employer}`,
              legalExcerpt: `Born in ${formData.cityOfBirth}, ${formData.countryOfBirth}. Currently employed as ${formData.occupation}.`,
              editIdx: 1
            }, {
              id: 'family',
              title: 'Family & Marital',
              icon: Users,
              status: milestoneStatuses.family,
              content: `Spouse: ${formData.maritalStatus === 'married' ? formData.spouseLegalName : 'None'}\nChildren: ${formData.hasChildren ? `${formData.children.length} children` : 'None'}`,
              legalExcerpt: `I declare that I am ${formData.maritalStatus === 'married' ? `married to ${formData.spouseLegalName}` : 'single'}.`,
              editIdx: 2
            }, {
              id: 'executor',
              title: 'Executors',
              icon: Shield,
              status: milestoneStatuses.executor,
              content: !formData.executor ? 'Not decided yet' : `Primary: ${formData.executor.split(' (')[0]}`,
              legalExcerpt: !formData.executor ? '[Pending User Decision]' : `I appoint ${formData.executor.split(' (')[0].toUpperCase()} to be the Executor.`,
              editIdx: 3
            }, {
              id: 'funeral',
              title: 'Funeral Wishes',
              icon: Heart,
              status: milestoneStatuses.funeral,
              content: formData.funeralArrangement || 'Not decided',
              legalExcerpt: formData.funeralArrangement === 'leave_to_executor' ? 'I leave all funeral decisions to my Executor.' : `I direct that my remains be ${formData.funeralArrangement || '[ARRANGEMENT]'}.`,
              editIdx: 4
            }, {
              id: 'personalGifts',
              title: 'Personal Gifts',
              icon: Gift,
              status: milestoneStatuses.personalGifts,
              content: formData.hasPersonalProperty ? `${formData.personalPropertyGifts.length} gifts` : 'No gifts',
              legalExcerpt: 'I direct my Trustee to distribute the following specific gifts...',
              editIdx: 5
            }, {
              id: 'specialAssets',
              title: 'Special Assets',
              icon: Star,
              status: milestoneStatuses.specialAssets,
              content: formData.hasArtCollectibles ? `${formData.artCollectibles.length} items` : 'No special assets',
              legalExcerpt: 'Special assets clause...',
              editIdx: 6
            }, {
              id: 'financialGifts',
              title: 'Financial & Charity',
              icon: DollarSign,
              status: milestoneStatuses.financialGifts,
              content: formData.distributionType === 'split' ? 'Split among beneficiaries' : 'Spouse first, then children',
              legalExcerpt: 'I direct my Trustee to divide the residue of my estate...',
              editIdx: 7
            }, {
              id: 'realEstate',
              title: 'Real Estate',
              icon: Home,
              status: milestoneStatuses.realEstate,
              content: formData.hasRealEstateGifts ? `${formData.realEstateGifts.length} properties` : 'No real estate gifts',
              legalExcerpt: 'Real property distribution clause...',
              editIdx: 8
            }].map(section => <ReviewSection key={section.id} id={section.id} title={section.title} icon={section.icon} status={section.status} content={section.content} legalExcerpt={section.legalExcerpt} isExpanded={!!expandedReviewSections[section.id]} onToggle={() => toggleReviewSection(section.id)} onEdit={() => navigateToEdit(section.editIdx)} data-id="element-4633">
                  {section.id === 'executor' && !formData.executor && <div className="p-3 sm:p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4" data-id="element-4634">
                      <p className="text-amber-800 font-medium mb-1 sm:mb-2 text-sm sm:text-base" data-id="element-4635">
                        Executor not yet selected
                      </p>
                      <p className="text-xs sm:text-sm text-amber-700 mb-3 sm:mb-4" data-id="element-4636">
                        Your Will cannot be finalized without naming an
                        executor.
                      </p>
                      <button onClick={() => navigateToEdit(section.editIdx)} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white border border-amber-300 text-amber-800 rounded-lg text-xs sm:text-sm font-medium hover:bg-amber-100 transition-colors" data-id="element-4637">
                        Resolve Now
                      </button>
                    </div>}
                </ReviewSection>)}

              {readiness.isReady && <div className="mt-8 bg-aliceBlue border border-borderColor rounded-xl p-6 sm:p-8 text-center flex flex-col items-center" data-id="element-4638">
                  <div className="w-16 h-16 bg-turquoise/10 rounded-full flex items-center justify-center mb-4" data-id="element-4639">
                    <CheckCircle className="w-8 h-8 text-turquoise" data-id="element-4640" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-primary mb-2" data-id="element-4641">
                    Ready to sign
                  </h3>
                  <p className="text-sm sm:text-base text-secondary mb-6 max-w-md" data-id="element-4642">
                    You've completed all sections. Your Will is ready to be
                    finalized or reviewed by a lawyer.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-4643">
                    <button onClick={() => {
                  setDraftStatus('submitted_for_review');
                  alert('Your Will has been submitted for lawyer review.');
                }} className="px-6 py-3 bg-white border border-borderColor text-primary rounded-xl font-medium hover:bg-white/80 transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-4644">
                      <Shield className="w-4 h-4" data-id="element-4645" /> Request Lawyer Review
                    </button>
                    <button onClick={() => {
                  alert('Your Will has been finalized! In production, this would generate the final legal document for signing.');
                  setDraftStatus('completed');
                }} className="px-6 py-3 bg-violetBlue text-white rounded-xl font-medium hover:bg-violetBlue/90 transition-colors flex items-center justify-center gap-2 text-sm shadow-sm" data-id="element-4646">
                      <FileCheck className="w-4 h-4" data-id="element-4647" /> Finalize Document
                    </button>
                  </div>
                </div>}
            </div>
          </div>

          <div className="lg:col-span-1" data-id="element-4648">
            <ReadinessWidget readiness={readiness} milestoneStatuses={milestoneStatuses} formData={formData} isSidebar onFinalize={() => {
            setShowWillPreview(true);
          }} onRequestReview={() => {
            setDraftStatus('submitted_for_review');
            alert('Your Will has been submitted for lawyer review.');
          }} data-id="element-4649" />
          </div>
        </div>
      </div>
    </div>;
  return <div className="flex flex-col h-screen bg-white" data-id="element-4650">
      <DashboardHeader categories={['Personal', 'Estate']} onMenuClick={handleMenuClick} onNavigateToAICopilot={handleNavigateToAICopilot} onNavigate={handleNavigate} data-id="element-4651" />

      <main className="flex-1 overflow-hidden flex flex-col relative" data-id="element-4652">
        {viewState === 'landing' && renderLandingState()}
        {viewState === 'setup' && renderSetupState()}
        {viewState === 'resume' && renderResumeState()}
        {viewState === 'builder' && renderBuilderState()}
        {viewState === 'review' && renderReviewState()}
      </main>

      {/* Upgrade Plan Modal */}
      <AnimatePresence data-id="element-4653">
        {showUpgradeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-4654">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowUpgradeModal(false)} data-id="element-4655" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2
        }} className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-4656">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-[#1a2d7a] p-6 sm:p-8 text-white rounded-t-2xl" data-id="element-4657">
                <button onClick={() => setShowUpgradeModal(false)} className="absolute top-4 right-4 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" data-id="element-4658">
                  <XCircle className="w-5 h-5 text-white" data-id="element-4659" />
                </button>
                <div className="flex items-center gap-4" data-id="element-4660">
                  <div className="p-3 bg-white/10 rounded-xl flex-shrink-0" data-id="element-4661">
                    <Crown className="w-8 h-8 text-white" data-id="element-4662" />
                  </div>
                  <div data-id="element-4663">
                    <h2 className="text-lg sm:text-xl font-bold mb-1" data-id="element-4664">
                      Upgrade to Create More Wills
                    </h2>
                    <p className="text-sm text-white/80 leading-relaxed" data-id="element-4665">
                      You've reached your plan limit. Upgrade to unlock
                      additional wills and keep protecting your family.
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8" data-id="element-4666">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-4667">
                  {/* What's included */}
                  <div data-id="element-4668">
                    <h3 className="text-sm font-bold text-primary mb-3" data-id="element-4669">
                      What's included with upgrade
                    </h3>
                    <ul className="space-y-2.5" data-id="element-4670">
                      {['Create up to 10 legally valid Wills', 'Province-specific legal compliance', 'Guided step-by-step builder', 'Appoint executors and guardians', 'Distribute assets and specific gifts', 'Optional lawyer review add-on', 'Save, resume, and update anytime'].map(item => <li key={item} className="flex items-start gap-2 text-sm text-secondary" data-id="element-4671">
                          <CheckCircle className="w-3.5 h-3.5 text-turquoise flex-shrink-0 mt-0.5" data-id="element-4672" />
                          <span data-id="element-4673">{item}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Pricing */}
                  <div className="bg-aliceBlue border border-borderColor rounded-xl p-5" data-id="element-4674">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-violetBlue/10 text-violetBlue rounded-full text-xs font-semibold tracking-wide uppercase mb-3" data-id="element-4675">
                      <Crown className="w-3 h-3" data-id="element-4676" />
                      Recommended
                    </span>
                    <h4 className="text-base font-bold text-primary mb-1" data-id="element-4677">
                      LawVo Pro+
                    </h4>
                    <p className="text-xs text-secondary mb-4" data-id="element-4678">
                      Everything in your current plan, plus expanded Will
                      access.
                    </p>

                    <div className="mb-4" data-id="element-4679">
                      <div className="flex items-baseline gap-1" data-id="element-4680">
                        <span className="text-2xl font-bold text-primary" data-id="element-4681">
                          $14.99
                        </span>
                        <span className="text-sm text-secondary" data-id="element-4682">/month</span>
                      </div>
                      <p className="text-xs text-secondary mt-0.5" data-id="element-4683">
                        or $149.99/year (save 17%)
                      </p>
                    </div>

                    <div className="space-y-2 mb-5" data-id="element-4684">
                      <div className="flex items-center gap-2 text-xs text-secondary" data-id="element-4685">
                        <Shield className="w-3.5 h-3.5 text-turquoise flex-shrink-0" data-id="element-4686" />
                        <span data-id="element-4687">30-day money-back guarantee</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-secondary" data-id="element-4688">
                        <Shield className="w-3.5 h-3.5 text-turquoise flex-shrink-0" data-id="element-4689" />
                        <span data-id="element-4690">Cancel anytime, no commitment</span>
                      </div>
                    </div>

                    <button onClick={() => {
                  setShowUpgradeModal(false);
                  handleNavigate('/my-account');
                }} className="w-full bg-violetBlue text-white py-3 rounded-xl font-semibold hover:bg-[#6844E8] transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-4691">
                      <Crown className="w-4 h-4" data-id="element-4692" />
                      Upgrade Now
                    </button>
                  </div>
                </div>

                {/* Footer link */}
                <div className="mt-5 pt-5 border-t border-borderColor text-center" data-id="element-4693">
                  <button onClick={() => {
                setShowUpgradeModal(false);
                handleNavigate('/my-account');
              }} className="text-sm text-secondary hover:text-primary font-medium transition-colors" data-id="element-4694">
                    Compare all plans →
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Will Preview Modal */}
      <AnimatePresence data-id="element-4695">
        {showWillPreview && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-4696">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowWillPreview(false)} data-id="element-4697" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2
        }} className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-4698">
              {/* Header */}
              <div className="px-6 sm:px-8 py-5 border-b border-borderColor flex items-center justify-between flex-shrink-0" data-id="element-4699">
                <div data-id="element-4700">
                  <h2 className="text-lg sm:text-xl font-bold text-primary" data-id="element-4701">
                    Your Will Preview
                  </h2>
                  <p className="text-xs sm:text-sm text-secondary mt-0.5" data-id="element-4702">
                    Review your legal document before finalizing.
                  </p>
                </div>
                <button onClick={() => setShowWillPreview(false)} className="p-1.5 rounded-lg hover:bg-aliceBlue transition-colors" data-id="element-4703">
                  <XCircle className="w-5 h-5 text-secondary" data-id="element-4704" />
                </button>
              </div>

              {/* Document Content */}
              <div className="flex-1 overflow-y-auto p-6 sm:p-8 md:p-12 bg-white" data-id="element-4705">
                <div className="max-w-2xl mx-auto" data-id="element-4706">
                  <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 uppercase tracking-widest font-serif text-gray-800" data-id="element-4707">
                    Last Will and Testament
                  </h3>
                  <p className="text-center mb-8 italic font-serif text-gray-800" data-id="element-4708">
                    of
                  </p>
                  <h4 className="text-lg sm:text-xl font-bold text-center mb-10 font-serif text-gray-800" data-id="element-4709">
                    {formData.fullName.toUpperCase() || '[FULL NAME]'}
                  </h4>
                  <LegalPreview testatorName={formData.fullName} province={formData.province} clauses={getLegalClauses()} isReviewMode isReady={readiness.isReady} data-id="element-4710" />
                </div>
              </div>

              {/* Footer Actions */}
              <div className="px-6 sm:px-8 py-4 border-t border-borderColor flex flex-col sm:flex-row gap-3 justify-end flex-shrink-0 bg-aliceBlue/30" data-id="element-4711">
                <button onClick={() => setShowWillPreview(false)} className="px-5 py-2.5 bg-white border border-borderColor text-secondary rounded-xl font-medium hover:bg-aliceBlue transition-colors text-sm" data-id="element-4712">
                  Close
                </button>
                <button onClick={() => {
              setDraftStatus('submitted_for_review');
              setShowWillPreview(false);
              alert('Your Will has been submitted for lawyer review.');
            }} className="px-5 py-2.5 bg-white border border-borderColor text-primary rounded-xl font-medium hover:bg-aliceBlue transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-4713">
                  <Shield className="w-4 h-4" data-id="element-4714" /> Request Lawyer Review
                </button>
                <button onClick={() => {
              setDraftStatus('completed');
              setShowWillPreview(false);
              alert('Your Will has been finalized! In production, this would generate the final legal document for signing.');
            }} className="px-5 py-2.5 bg-violetBlue text-white rounded-xl font-medium hover:bg-violetBlue/90 transition-colors flex items-center justify-center gap-2 text-sm shadow-sm" data-id="element-4715">
                  <FileCheck className="w-4 h-4" data-id="element-4716" /> Finalize & Download
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}